// FUNCTIONALITY-101/102 — Store V2 isolated app orchestrator.
// No /me, no session gate, no cross-origin catalog fetch. Anonymous browsing.
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { bootstrapTelegramWebApp } from "../lib/telegram.js";
import "./store-v2.css";
import "./store-v2-np-reference.css";
import { fetchCatalogCategories } from "../api/client.js";
import type {
  CatalogV2Bundle,
  CatalogV2Category,
  CatalogV2Meta,
  CatalogV2Product,
} from "./catalog-v2-types.js";
import {
  loadCatalogMetaV2,
  loadCatalogV2,
  loadCategoryPage,
  peekCategoryPageCache,
  prefetchCategoryPage,
  POPULAR_CATEGORY_PREFETCH_IDS,
  loadNewArrivalsPage,
  prefetchNewArrivalsPage1,
  prefetchBrandIndexV2,
  loadBrandIndexV2,
  prefetchCountryIndexV2,
  loadCountryIndexV2,
  loadBrandPage,
  loadCountryPage,
  peekBrandPageCache,
  peekCountryPageCache,
  productsForCategory,
  resetCatalogV2Cache,
  revalidateCatalogV2IfStale,
} from "./catalog-v2-data.js";
import { invalidateCustomerLocalProductsCache } from "./catalog-local-products-v2.js";
import { normalizeCatalogSortKey, sortCatalogProducts, type CatalogSortKey } from "./catalog-sort-v2.js";
import {
  addToCart,
  clearCart,
  updateItemPrice,
  decItem,
  getCart,
  getPromoCode,
  incItem,
  normalizePromoCode,
  removeItem,
  setPromoCode as persistPromoCode,
  subtotalOf,
  evaluatePromo,
  writeCart,
  type CartV2Item,
} from "./cart-v2.js";
import { foldToken, pickZeroResultPhrase, type SearchableProduct } from "./search-v2.js";
import { isLikelyRussianFoodQuery, RU_SEARCH_SUCCESS_HINT_UK } from "./search-aliases-v2.js";
import { markSearchPerf } from "./search-perf-marks-v2.js";
import { beginNewSearchSession, commitSubmittedQuery } from "./search-session-v2.js";
import {
  hitToCatalogProduct,
  serverSearch,
  type ServerSearchHit,
} from "./search-server-client-v2.js";
import { SearchDiscoveryContinuationV2 } from "./SearchDiscoveryContinuationV2.js";
import { STORE_V2_TILES } from "./taxonomy-v2.js";
import {
  Chrome,
  LoadingBlock,
  BrandedStoreLoading,
  EmptyState,
  RouteDegradedState,
  ManagerContactButton,
  Toast,
  StoreV2GlobalClose,
  type ContactToastKind,
} from "./components.js";
import { useRouteLoadingGate } from "./route-loading-gate-v2.js";
import { LazyVisualSearchSlot } from "./LazyVisualSearchSlot.js";
import { GlobalAppCloseProvider, isTelegramMiniAppContext } from "@asiastyle/surface-ui";
import "@asiastyle/surface-ui/workspace.css";
import {
  applyStoreV2Theme,
  cycleStoreV2Theme,
  readStoreV2Theme,
  writeStoreV2Theme,
  type StoreV2Theme,
} from "./theme-v2.js";
import { HomeV2 } from "./HomeV2.js";
import { HomeMarketingOptInCardV2 } from "./HomeMarketingOptInCardV2.js";
import {
  HOME_MARKETING_COPY_VERSION_CLIENT,
  markEntryMarketingOptionSeen,
  readHomeMarketingOptInMemory,
  shouldShowHomeMarketingOptIn,
  writeHomeMarketingOptInMemory,
} from "./home-marketing-optin-v2.js";
import { RecoveryCardV2, type RecoveryCardView } from "./RecoveryCardV2.js";
import { fetchActiveRecovery, markCheckoutRecoveryProgress, revalidateCartAgainstCatalog, syncCartRecoveryToServer } from "./recovery-client-v2.js";
import { CatalogListV2 } from "./CatalogListV2.js";
import { collectionKeyFrom } from "./collection-nav-state-v2.js";
import {
  clearSessionHomeScroll,
  readSessionHomeScroll,
  shouldRestoreBackScroll,
  shouldRestoreHomeScroll,
  writeSessionHomeScroll,
} from "./navigation-restoration-policy-v2.js";
import {
  categoryProductsQueryKey,
  firstPaintProductIds,
  resolveCategorySortBeforeFetch,
} from "./category-query-v2.js";
import { ProductV2 } from "./ProductV2.js";
import { cartSkuQuantity, cartTotalQuantity } from "./cart-quantity-v2.js";
import { CartV2View } from "./CartV2View.js";
import { CheckoutV2View } from "./CheckoutV2View.js";
import { OrderPaymentPanelV2 } from "./OrderPaymentPanelV2.js";
import { BankIbanPaymentSheetV2 } from "./BankIbanPaymentSheetV2.js";
import {
  fetchBankInstructionV2,
  pollAsiaStyleBankPaymentStatus,
  BANK_RESUME_POLL_MS,
  type BankPaymentClientPayload,
} from "./bank-iban-payment-v2.js";
import { isNovapayCheckoutDegradedStaging } from "./novapay-staging-degraded-v2.js";
import {
  PaymentMethodChangeSheetV2,
  type PaymentMethodChoiceId,
} from "./PaymentMethodChangeSheetV2.js";
import { enabledCustomerCheckoutPaymentMethods } from "./customer-payment-flags-v2.js";
import { ConsentDetails, ConsentGate, ProfileV2 } from "./about-v2.js";
import { EngagementHubPlaceholderV2 } from "./EngagementHubPlaceholderV2.js";
import { resolveEngagementHubContract } from "./engagement-hub-v2.js";
import { CONSENT_VERSION, STORE_INFO } from "./store-info-v2.js";
import { mergePdpHydrationKeepingCommercialPrice, resolveCustomerSellPrice, resolveCustomerVisiblePrice } from "./resolve-customer-visible-price-v2.js";
import { resolveCustomerFacingCode } from "./product-identity-v2.js";
import { emitEvent, initEvents } from "./events-v2.js";
import { recordCustomerProductionBootAttestation } from "./production-release-attestation-v2.js";
import {
  bumpNavigationId,
  noteScrollDepthBucket,
  recordRouteLoadDuration,
  setObservabilityBuildId,
  setObservabilityRoute,
} from "./observability-ring-v2.js";
import { emitStagingLaunchDiagnostics } from "./store-v2-launch-diagnostics.js";
import { DiagnosticsV2 } from "./DiagnosticsV2.js";
import { StagingHardRestoreDiagnostics } from "./StagingHardRestoreDiagnostics.js";
import { ImeForensicDebugV2, isOwnerImeDebugAllowed } from "./ImeForensicDebugV2.js";
import { resolveProductTitle } from "./product-title-resolver.js";
import { recommendForProduct } from "./cross-sell-rules.js";
import { getCartJourneyRecommendations, type ProductRecommendationItem } from "./product-recommendations-v2.js";
import { loadCartRecommendationPool } from "./cart-merch-load-v2.js";
import {
  fetchOnsiteRecommendations,
  mapOnsiteToCrossSell,
  validateOnsiteAdd,
} from "./onsite-recommendations-v2.js";
import { HomeOnsiteRecommendationsV2 } from "./HomeOnsiteRecommendationsV2.js";
import { registerActionableProducts } from "./customer-actionable-product-v2.js";
import {
  resolveCatalogProductByAnyId,
  resolveCustomerSellability,
  sellabilityBlocksAdd,
  sellabilityMessageUk,
} from "./customer-sellability-v2.js";
import { readStoreV2Lang, writeStoreV2Lang, type StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { tileLabel, tileName } from "./taxonomy-v2.js";
import { resolveManagerPublicUrl } from "./contact-manager-v2.js";
import { readCheckoutPrefill, writeCheckoutPrefill } from "./checkout-prefill-v2.js";
import { persistLegalConsentRemote } from "./legal-consent-client-v2.js";
import {
  applyBootstrapToLocalCache,
  fetchCustomerBootstrap,
  getCachedCustomerBootstrap,
  type CustomerBootstrapPayload,
} from "./customer-runtime-bootstrap-v2.js";
import { OwnerRuntimeStripV2 } from "./OwnerRuntimeStripV2.js";
import { OwnerBuildIdentityChipV2 } from "./OwnerBuildIdentityChipV2.js";
import { markPdpPerf, resetPdpPerf } from "./pdp-seed-perf-v2.js";
import { enforceCustomerRuntimeHandshake } from "./customer-runtime-handshake-v2.js";
import {
  currentIdentityScope,
  ensureIdentityScopedCaches,
  persistConsent as persistConsentProfile,
  readConsentAccepted as readConsentAcceptedProfile,
  readCheckoutPrefill as readProfilePrefill,
  telegramUserId,
  clearMutableFirstRunCustomerState,
} from "./customer-profile-v2.js";
import { resolveCanonicalCustomerName } from "./canonical-customer-name-v2.js";
import {
  COMMERCE_CANARY_163A_R2B_HARD_RESTORE_MARKER,
  ensureStoreV2CacheEpoch,
  STORE_V2_CACHE_EPOCH,
} from "./store-v2-cache-epoch-v2.js";
import { customerOrderActionV2 } from "./order-actions-v2.js";
import { getNovapayPaymentStatusV2 } from "./novapay-checkout-v2.js";
import {
  readPayretStartParam,
  restorePayretContext,
  scheduleBoundedRetries,
  isDocumentsStartParam,
  isPaymentReminderStartParam,
} from "./payret-restore-v2.js";
import {
  createReferralLink,
  readReferralCodeFromLaunch,
  recordReferralClick,
  attributeReferralStartapp,
} from "./checkout-reservation-v2.js";
import {
  attributeGrowthStartapp,
  dispatchStartParam,
  readLaunchStartParam,
} from "./telegram-startapp-dispatcher-v2.js";
import { ContactManagerModal } from "./contact-actions-v2.js";
import { BONUS_RULES_EFFECTIVE_DATE, BONUS_RULES_VERSION } from "./store-info-i18n-v2.js";
import { BrandBrowserV2 } from "./BrandBrowserV2.js";
import { buildBrandIndex, brandSlug, mapBrandIndexRows, type CatalogBrand } from "./brand-browser-v2.js";
import { CountryBrowserV2 } from "./CountryBrowserV2.js";
import { buildCountryIndex, mapCountryIndexRows, type CatalogCountry } from "./country-browser-v2.js";
import { ReferralCardV2, type ReferralShareData } from "./ReferralCardV2.js";
import {
  consumeReferredFirstOrderSlot,
  isReferredSession,
  markReferredSession,
} from "./referral-conversion-v2.js";
import { CustomerBottomNav, type CustomerBottomNavId } from "./CustomerBottomNavV2.js";
import { PaidBentoDashboardV2 } from "./PaidBentoDashboardV2.js";
import { ReferralExitRescueSheetV2 } from "./ReferralExitRescueSheetV2.js";
import {
  canShowReferralExitRescue,
  markReferralExitDismissed,
  markReferralExitShown,
  markReferralSharedForOrder,
  wasReferralSharedForOrder,
} from "./referral-exit-rescue-v2.js";
import { buildInviteSharePayload, openTelegramShareSync } from "./referral-share-v2.js";
import {
  resolveNovapayUiPhase,
  scrubStalePaymentReturnQuery,
  canChangePaymentMethod,
} from "./payment-lifecycle-v2.js";
import { CommercePageShell, StatusHeroCard, ActionGrid, ActionTile, TertiaryTextAction } from "./commerce-ui-v2.js";
import { buildManagerContactContext } from "./manager-source-context-v2.js";
import { requestProductAvailability } from "./availability-request-v2.js";
import { AvailabilityRequestSheet } from "./AvailabilityRequestSheet.js";
import { QuickScrollControls } from "./quick-scroll-v2.js";
import { SearchPerfDiagHud } from "./SearchPerfDiagHud.js";
import {
  beginReorderCartOperation,
  buildReorderSessionFromSnapshot,
  buildReorderSnapshot,
  cartHasPaymentFields,
  isActiveReorderSession,
  mergeReorderIntoCart,
  peekReorderSession,
  patchReorderSession,
  reorderAnalyticsBase,
  reorderCartBannerUk,
  writeReorderSession,
  type ReorderSourceInput,
} from "./reorder-v2.js";
// R7.2.2C1 — Account cabinet routes are EAGER. Lazy chunks were omitted from an
// incomplete C production rollback → Orders/Messages/Invite/OrderDetail failed
// while Documents (eager in Profile) still worked. Do not re-lazy these without
// a deploy verifier that proves every __vite__mapDeps asset is on the alias.
import { MyOrdersV2 } from "./MyOrdersV2.js";
import { MessagesV2 } from "./MessagesV2.js";
import { OrderDetailV2 } from "./OrderDetailV2.js";
import { InviteFriendsV2 } from "./InviteFriendsV2.js";
const PromotionsV2 = lazy(() => import("./PromotionsV2.js").then((m) => ({ default: m.PromotionsV2 })));
import { ExitRetentionSheetV2 } from "./ExitRetentionSheetV2.js";
import { resolveExitRetentionState, syncExitClosingConfirmation } from "./exit-retention-v2.js";
import { recordRecentProduct, readRecentProductIds } from "./recent-products-v2.js";
import type { AvailabilityAttachment } from "./availability-attachment-v2.js";
import {
  armPostOrderCloseGuard,
  clearPersistedSuccessView,
  closeStoreExplicitly,
  disarmPostOrderCloseGuard,
  persistSuccessView,
  readPersistedSuccessView,
} from "./post-order-session-v2.js";
import {
  hideTelegramBackButton,
  STORE_V2_BROWSER_CLOSE_HINT_EN,
  STORE_V2_BROWSER_CLOSE_HINT_UA,
} from "./telegram-store-v2-navigation.js";
import {
  createCustomerNavigationService,
  normalizeCustomerOrderRef,
  stackForRoute,
} from "./customer-navigation-service-v2.js";
import {
  mountTelegramBackController,
  resolveChromeBackAction,
} from "./telegram-back-controller-v2.js";
import { CustomerRouteErrorBoundary } from "./CustomerRouteErrorBoundary.js";
import { PublicLegalViewer } from "./public-legal-v2.js";
import {
  resolvePublicLegalPanel,
  stagingPublicLegalUrl,
  type LegalHubPanel,
} from "./legal-document-registry-v2.js";
import {
  legalSourceSkipsHub,
  setLegalNavContext,
  peekLegalNavContext,
  clearLegalNavContext,
  type LegalNavSource,
} from "./legal-nav-context-v2.js";
import { consumeStagingQaResetConsent, clearStagingQaConsentResetSessionFlag, wasStagingQaConsentResetThisSession } from "./staging-qa-v2.js";
import { DirectBrowserEntry } from "./direct-browser-entry-v2.js";
import {
  hasTelegramMiniAppContext,
  readDirectBrowserContinueAllowed,
  clearDirectBrowserContinueAllowed,
} from "./telegram-webapp-context-v2.js";
import {
  hasActivePaymentResume,
  readPaymentResume,
  persistPaymentResume,
  clearPaymentResume,
  patchPaymentResumeFlowState,
} from "./payment-resume-v2.js";
import type { PaymentMethodChoice } from "./checkout-validation-v2.js";

function storeScrollRoot(): HTMLElement | null {
  try {
    return document.querySelector<HTMLElement>("[data-store-scroll-root='true'], .sv2-root");
  } catch {
    return null;
  }
}

type NavIntent = "forward" | "back" | "home" | "restore" | "idle";

/** FUNCTIONALITY-115C — stabilize scroll against sticky header + late image layout. */
function applyStoreScroll(top: number, reason: string): void {
  void reason;
  const apply = () => {
    try {
      const root = storeScrollRoot();
      if (root) {
        root.scrollTop = top;
        root.scrollTo({ top, left: 0, behavior: "auto" });
      } else {
        window.scrollTo({ top, left: 0, behavior: "auto" });
      }
    } catch {
      window.scrollTo(0, top);
    }
  };
  window.requestAnimationFrame(() => {
    apply();
    window.requestAnimationFrame(() => {
      apply();
      // Final layout stabilization after images / sticky header settle.
      window.setTimeout(apply, 48);
      window.setTimeout(apply, 160);
    });
  });
}

export function scrollStoreToTop(reason: string): void {
  applyStoreScroll(0, reason);
}

function readStoreScrollTop(): number {
  try {
    const root = storeScrollRoot();
    return root?.scrollTop ?? window.scrollY ?? 0;
  } catch {
    return 0;
  }
}

function restoreStoreScroll(top: number): void {
  applyStoreScroll(top, "back_restore");
}

// FUNCTIONALITY-104/111 — build id follows the Pages deploy (VITE_BUILD_ID).
export const STORE_V2_BUILD_ID =
  (import.meta.env.VITE_BUILD_ID as string | undefined)?.trim() ||
  COMMERCE_CANARY_163A_R2B_HARD_RESTORE_MARKER;
/** Bundle marker for human proof / deploy verification. */
export const COMMERCE_CANARY_163A_R2_ORDER_SYNC_MARKER = "COMMERCE-CANARY-163A-R2-ORDER-SYNC";
/** NovaPay general production remains ON (168D6 invariant; must stay in production bundle). */
export const COMMERCE_LAUNCH_168D6_HARD_STABILIZATION = "COMMERCE-LAUNCH-168D6-HARD-STABILIZATION";
void COMMERCE_CANARY_163A_R2_ORDER_SYNC_MARKER;
void STORE_V2_CACHE_EPOCH;
void COMMERCE_CANARY_163A_R2B_HARD_RESTORE_MARKER;
if (typeof document !== "undefined") {
  document.documentElement.setAttribute("data-as-168d6", COMMERCE_LAUNCH_168D6_HARD_STABILIZATION);
  document.documentElement.setAttribute("data-customer-build-id", STORE_V2_BUILD_ID);
  document.documentElement.setAttribute(
    "data-customer-environment",
    String(import.meta.env.VITE_STAGING_DIAGNOSTICS ?? "") === "1" ? "staging" : "production",
  );
  document.documentElement.setAttribute(
    "data-payment-mode",
    String(import.meta.env.VITE_CUSTOMER_PAYMENT_MODE ?? "").trim() || "UNSET",
  );
}
const SESSION_KEY_STORE = "asiastyle.store-v2.session.v1";
type View =
  | { name: "home" }
  | { name: "category"; categoryId: string }
  | { name: "search"; query: string }
  | { name: "product"; productId: string }
  | { name: "cart" }
  | { name: "checkout" }
  | { name: "profile" }
  | { name: "profile-edit"; tab?: "personal" | "fop" | "company" }
  | { name: "delivery-addresses" }
  /** @deprecated CUSTOMER-R5 — redirects to profile-edit tab=fop */
  | { name: "buyer-profiles" }
  | { name: "new" }
  | { name: "promotions" }
  | { name: "dropshipping" }
  | { name: "bonuses" }
  | { name: "brands" }
  | { name: "brand"; slug: string }
  | { name: "countries" }
  | { name: "country"; countryId: string }
  | { name: "success"; orderId: string; displayNumber?: string | undefined; status?: string | undefined; paymentMethod?: string | undefined; totalUah?: number | undefined; contactName?: string | undefined; contactPhone?: string | undefined; bankPayment?: BankPaymentClientPayload | null | undefined; bankSheetOpen?: boolean | undefined; paymentTiming?: "immediate" | "after_manager_confirmation" | undefined; autoBankHandoff?: boolean | undefined }
  // STAGING-LAUNCH-READINESS-002-R2 — customer cabinet.
  | { name: "orders" }
  | { name: "order-detail"; orderId: string }
  | { name: "messages" }
  | { name: "invite-friends" }
  | { name: "engagement-hub" }
  /** COMMERCE-LAUNCH-168D2 — owner-only IME forensic (?ime_debug=1 + allowlist). */
  | { name: "ime-debug" };

function getSessionKey(): string {
  const scope = currentIdentityScope();
  const scoped = `${SESSION_KEY_STORE}.scope.${scope}`;
  try {
    const existing = window.localStorage.getItem(scoped);
    if (existing) return existing;
    const created = `storev2-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(scoped, created);
    window.localStorage.setItem(SESSION_KEY_STORE, created);
    return created;
  } catch {
    return `storev2-${Date.now()}`;
  }
}

/** R1G10F6 — append mgr_cat_* rows from Worker categories API onto static meta. */
function overlayDynamicCategoriesFromApi(
  base: CatalogV2Category[],
  apiRows: Array<{ id?: string; label?: string; name?: string; product_count?: number; sort?: number }>,
): CatalogV2Category[] {
  const seen = new Set(base.map((c) => c.id));
  const extra: CatalogV2Category[] = [];
  for (const row of apiRows) {
    const id = String(row.id ?? "").trim();
    if (!id.startsWith("mgr_cat_") || seen.has(id)) continue;
    seen.add(id);
    extra.push({
      id,
      label: String(row.label ?? row.name ?? id),
      sort: typeof row.sort === "number" ? row.sort : 100 + extra.length,
      count: Number(row.product_count ?? 0),
    });
  }
  return extra.length ? [...base, ...extra] : base;
}

function currentTelegramFirstName(): string | null {
  try {
    const user = (window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { first_name?: string | null } } } };
    }).Telegram?.WebApp?.initDataUnsafe?.user;
    const name = String(user?.first_name ?? "").trim();
    return name || null;
  } catch {
    return null;
  }
}

function isSyntheticName(name: string | null | undefined): boolean {
  const v = String(name ?? "").trim().toLowerCase();
  if (!v) return true;
  return (
    v === "lklsk" ||
    v === "test" ||
    v === "demo" ||
    v.includes("synthetic") ||
    v.includes("smoke") ||
    v.startsWith("user_")
  );
}

function readConsentAccepted(): boolean {
  return readConsentAcceptedProfile(CONSENT_VERSION);
}

function persistConsent(lang: StoreV2Lang): void {
  persistConsentProfile({ version: CONSENT_VERSION, locale: lang, tenant: "asiastyle" });
}

export function StoreV2App() {
  const publicLegalPanel = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("publicLegal") !== "1") return null;
      return resolvePublicLegalPanel(params.get("legal")) ?? ("about" as LegalHubPanel);
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    bootstrapTelegramWebApp();
  }, []);

  if (publicLegalPanel) {
    return (
      <GlobalAppCloseProvider>
        <PublicLegalViewer initialPanel={publicLegalPanel} />
      </GlobalAppCloseProvider>
    );
  }

  return (
    <GlobalAppCloseProvider>
      <StoreV2AppMain />
    </GlobalAppCloseProvider>
  );
}

function buildInitialStack(): View[] {
  const resume = readPaymentResume();
  if (resume?.order_id) {
    return [
      {
        name: "success",
        orderId: resume.order_id,
        displayNumber: resume.display_code ?? undefined,
        paymentMethod: resume.provider,
        status: resume.flow_state === "payment_paid_confirmed" ? "paid" : "pending",
      },
    ];
  }
  const saved = readPersistedSuccessView();
  if (!saved?.orderId) return [{ name: "home" }];
  return [
    {
      name: "success",
      orderId: saved.orderId,
      displayNumber: saved.displayNumber,
      status: saved.status,
      paymentMethod: saved.paymentMethod,
      totalUah: saved.totalUah,
      contactName: saved.contactName,
      contactPhone: saved.contactPhone,
    },
  ];
}

function StoreV2AppMain() {
  ensureStoreV2CacheEpoch();
  ensureIdentityScopedCaches();
  useEffect(() => {
    markSearchPerf("app_boot_start");
    bootstrapTelegramWebApp();
    markSearchPerf("shell_visible");
  }, []);

  const qaConsentReset = useMemo(() => consumeStagingQaResetConsent(), []);
  const [directBrowserOk, setDirectBrowserOk] = useState(
    () => hasTelegramMiniAppContext() || readDirectBrowserContinueAllowed(),
  );
  const [profileLegalPanel, setProfileLegalPanel] = useState<LegalHubPanel | null>(null);
  const [bundle, setBundle] = useState<CatalogV2Bundle | null>(null);
  /** R4A — meta-only home bootstrap (tiles) before full catalog parse. */
  const [homeMeta, setHomeMeta] = useState<CatalogV2Meta | null>(null);
  const [loadError, setLoadError] = useState(false);
  /**
   * CUSTOMER-R4.15-C — category incremental browse (first page without full cards JSON).
   * Search remains GLOBAL_CATALOG via openSearch — never scoped to these pages.
   */
  const [categoryBrowse, setCategoryBrowse] = useState<{
    categoryId: string;
    sort: CatalogSortKey;
    products: CatalogV2Product[];
    page: number;
    total: number;
    hasMore: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
  } | null>(null);
  const categoryLoadGenRef = useRef(0);
  const categoryAbortRef = useRef<AbortController | null>(null);
  const [newArrivalsBrowse, setNewArrivalsBrowse] = useState<{
    products: CatalogV2Product[];
    page: number;
    total: number;
    hasMore: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    sort: CatalogSortKey;
    /** Full set loaded + client-sorted (legacy shards without sort keys). */
    localComplete: boolean;
  } | null>(null);
  const [brandIndex, setBrandIndex] = useState<CatalogBrand[]>([]);
  const [countryIndex, setCountryIndex] = useState<CatalogCountry[]>([]);
  const [brandBrowse, setBrandBrowse] = useState<{
    slug: string;
    products: CatalogV2Product[];
    page: number;
    total: number;
    hasMore: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    sort: CatalogSortKey;
    localComplete: boolean;
  } | null>(null);
  const [countryBrowse, setCountryBrowse] = useState<{
    countryId: string;
    products: CatalogV2Product[];
    page: number;
    total: number;
    hasMore: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    sort: CatalogSortKey;
    localComplete: boolean;
  } | null>(null);
  const brandLoadGenRef = useRef(0);
  const countryLoadGenRef = useRef(0);
  const brandsNavLockRef = useRef(false);
  const countriesNavLockRef = useRef(false);
  const newArrivalsLoadGenRef = useRef(0);
  /** CUSTOMER-172A — server search hits (no client V5 full-catalog scan). */
  const [serverSearchHits, setServerSearchHits] = useState<ServerSearchHit[]>([]);
  const [serverSearchMeta, setServerSearchMeta] = useState<{
    query: string;
    correction: string | null;
    correctionKind: string | null;
    didYouMean: string | null;
    total: number;
    catalogRevision: string | null;
    pending: boolean;
    error: boolean;
    requestId: number;
  } | null>(null);
  const [searchSlowPending, setSearchSlowPending] = useState(false);
  const productShellByIdRef = useRef<Map<string, CatalogV2Product>>(new Map());
  const searchInflightRef = useRef<{ id: number; abort: AbortController | null }>({
    id: 0,
    abort: null,
  });
  const { showSkeleton: catalogShowSkeleton, timedOut: catalogTimedOut } = useRouteLoadingGate(
    !bundle && !homeMeta && !loadError,
    { skeletonDelayMs: 0 },
  );
  const [stack, setStack] = useState<View[]>(() => (qaConsentReset ? [{ name: "home" }] : buildInitialStack()));
  const [cart, setCart] = useState<CartV2Item[]>([]);
  const [recoveryPayload, setRecoveryPayload] = useState<Record<string, unknown> | null>(null);
  const [journeyRecs, setJourneyRecs] = useState<ProductRecommendationItem[]>([]);
  const [journeyRecCanRefresh, setJourneyRecCanRefresh] = useState(false);
  const [journeyRecLoading, setJourneyRecLoading] = useState(false);
  const [journeyRecRefreshing, setJourneyRecRefreshing] = useState(false);
  const [journeyRecRotationIndex, setJourneyRecRotationIndex] = useState(0);
  const journeyRecPoolRef = useRef<CatalogV2Product[]>([]);
  const journeyRecCursorRef = useRef(0);
  const journeyRecLoadGenRef = useRef(0);
  const journeyRecRefreshLockRef = useRef(false);
  const [promoCode, setPromoCodeState] = useState<string | null>(null);
  // 103: consent is mandatory — catalog is not rendered until server bootstrap + accept.
  const [consentAccepted, setConsentAccepted] = useState<boolean>(false);
  const [customerBootstrap, setCustomerBootstrap] = useState<CustomerBootstrapPayload | null>(null);
  const [consentView, setConsentView] = useState<"gate" | "details" | "legal">("gate");
  const [consentLegalPanel, setConsentLegalPanel] = useState<LegalHubPanel | null>(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState(false);
  // R3.9 — wait for server bootstrap before catalog/search/cart/checkout (no localStorage gate skip).
  const [bootstrapping, setBootstrapping] = useState<boolean>(true);
  const [runtimeRevBlocked, setRuntimeRevBlocked] = useState(false);
  const catalogLoadRequestedRef = useRef(false);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [marketingSuppressed, setMarketingSuppressed] = useState(false);
  const [marketingPermissionState, setMarketingPermissionState] = useState<
    "UNKNOWN" | "GRANTED" | "WITHDRAWN" | "DENIED"
  >("UNKNOWN");
  const [homeMarketingOptInVisible, setHomeMarketingOptInVisible] = useState(false);
  const [contactToast, setContactToast] = useState<{ kind: ContactToastKind; text: string } | null>(null);
  const [contactModal, setContactModal] = useState(false);
  const [contactContext, setContactContext] = useState<Record<string, unknown> | null>(null);
  const contactButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastManagerContactKeyRef = useRef<string>("");
  const lastManagerContactAtRef = useRef(0);
  const [showDiag, setShowDiag] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [lang, setLang] = useState<StoreV2Lang>(() => readStoreV2Lang());
  const [referral, setReferral] = useState<ReferralShareData | null>(null);
  const [exitSheetOpen, setExitSheetOpen] = useState(false);
  /** CUSTOMER-MINIAPP-131 — visible direct-browser close fallback (never silent). */
  const [browserExitOpen, setBrowserExitOpen] = useState(false);
  const [scrollMemory, setScrollMemory] = useState<number[]>([0]);
  const homeScrollSessionRef = useRef(readSessionHomeScroll());
  const pendingRestoreRef = useRef<{ routeKey: string; top: number } | null>(null);
  const navIntentRef = useRef<NavIntent>("idle");
  // STAGING-LAUNCH-READINESS-002-R2-R2 — flips true on the first explicit in-app
  // navigation (push/back/navigateHomeNow); see the visibilitychange resume effect.
  const userNavigatedRef = useRef(false);
  const lastRouteKeyRef = useRef<string>("home");
  const bootTelemetrySentRef = useRef(false);
  const [theme, setTheme] = useState<StoreV2Theme>(() => readStoreV2Theme());
  const [availabilityFeedback, setAvailabilityFeedback] = useState<{
    productId: string;
    message: string;
  } | null>(null);
  const [availabilitySheetProduct, setAvailabilitySheetProduct] = useState<CatalogV2Product | null>(null);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [novapayLifecycleStatus, setNovapayLifecycleStatus] = useState<string | null>(null);
  /** STAGING-LAUNCH-READINESS-002-R2-R2 — guards the deferred-clear-on-paid effect below
   * so a NovaPay order only empties the cart once, the moment payment is confirmed. */
  const novapayCartClearedForOrderRef = useRef<string | null>(null);
  /** R7A — FOP receipt uploaded → manager review (not paid). */
  const [fopReviewActive, setFopReviewActive] = useState(false);
  const [bankSheetOpen, setBankSheetOpen] = useState(false);
  const [bankPayload, setBankPayload] = useState<BankPaymentClientPayload | null>(null);
  const [bankConfirmedPaid, setBankConfirmedPaid] = useState(false);
  const bankConfirmedRef = useRef(false);
  const successFopReceiptCtaRef = useRef<(() => void) | null>(null);
  /** R9-P0 — change payment method sheet on success unpaid (no session until continue). */
  const [successMethodSheetOpen, setSuccessMethodSheetOpen] = useState(false);
  const [successMethodBusy, setSuccessMethodBusy] = useState(false);
  const [successMethodSaveError, setSuccessMethodSaveError] = useState<string | null>(null);
  const [exitRescueOpen, setExitRescueOpen] = useState(false);
  const exitRescuePendingRef = useRef<null | (() => void)>(null);
  const [paymentReturn, setPaymentReturn] = useState<"success" | "fail" | null>(() => {
    try {
      const v = new URLSearchParams(window.location.search).get("payment_return");
      if (v === "success" || v === "fail") return v;
    } catch {
      /* ignore */
    }
    return null;
  });
  const returningCustomer = useMemo(() => {
    try {
      return readConsentAccepted() || readCheckoutPrefill() != null;
    } catch {
      return false;
    }
  }, []);
  const [welcomeNameTick, setWelcomeNameTick] = useState(0);
  const welcomeName = useMemo(() => {
    try {
      const p = readProfilePrefill();
      const boot = getCachedCustomerBootstrap();
      return resolveCanonicalCustomerName({
        profileName: p?.name?.trim() || boot?.profile?.first_name || null,
        nameSource: p?.name_source ?? (boot?.profile?.first_name ? "profile" : null),
        telegramFirstName: currentTelegramFirstName(),
      });
    } catch {
      return null;
    }
  }, [welcomeNameTick]);
  useEffect(() => {
    const bump = () => setWelcomeNameTick((n) => n + 1);
    window.addEventListener("asiastyle-profile-updated", bump);
    return () => window.removeEventListener("asiastyle-profile-updated", bump);
  }, []);
  const lastCheckoutSnapshot = useMemo(() => {
    try {
      return readCheckoutPrefill();
    } catch {
      return null;
    }
  }, []);
  const sessionKey = useMemo(getSessionKey, []);
  /** Production: never. Staging bake only when VITE_STAGING_DIAGNOSTICS=1 and ?diag=1. */
  const diagEnabled = useMemo(() => {
    try {
      const baked =
        String(import.meta.env.VITE_STAGING_DIAGNOSTICS ?? "").trim() === "1";
      if (!baked) return false;
      return new URLSearchParams(window.location.search).get("diag") === "1";
    } catch {
      return false;
    }
  }, []);

  const setLanguage = (next: StoreV2Lang) => {
    setLang(next);
    writeStoreV2Lang(next);
  };

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 1600);
  };

  /**
   * CUSTOMER-172A — Home must NOT initiate full catalog parse on the main thread.
   * Meta-only on Home; full catalog loads only when a browse surface explicitly needs it.
   */
  const requestCatalogLoad = (opts?: { full?: boolean }) => {
    const wantFull = opts?.full === true;
    if (!catalogLoadRequestedRef.current) {
      catalogLoadRequestedRef.current = true;
      void loadCatalogMetaV2()
        .then((m) => {
          setHomeMeta(m);
          markSearchPerf("shell_visible", { meta: true });
        })
        .catch(() => {
          /* meta failure is non-fatal when full load follows */
        });
      void fetchCatalogCategories()
        .then((res) => {
          const rows =
            res.categories?.length && res.categories.length > 0
              ? res.categories
              : [...(res.primary ?? []), ...(res.overflow ?? [])];
          setHomeMeta((prev) => {
            if (!prev) return prev;
            const categories = overlayDynamicCategoriesFromApi(prev.categories, rows);
            if (categories.length === prev.categories.length) return prev;
            return { ...prev, categories };
          });
          setBundle((prev) => {
            if (!prev) return prev;
            const categories = overlayDynamicCategoriesFromApi(prev.meta.categories, rows);
            if (categories.length === prev.meta.categories.length) return prev;
            return { ...prev, meta: { ...prev.meta, categories } };
          });
        })
        .catch(() => {
          /* dynamic overlay is best-effort */
        });
      if (!wantFull) return;
    } else if (!wantFull) {
      return;
    }
    loadCatalogV2()
      .then((b) => {
        setBundle(b);
        setHomeMeta(b.meta);
        emitEvent("category_loaded", { product_count: b.products?.length ?? 0 });
      })
      .catch(() => {
        setLoadError(true);
        emitEvent("category_load_failed", { reason: "catalog_fetch" });
      });
    if (wantFull) {
      void revalidateCatalogV2IfStale().then((fresh) => {
        if (fresh) setBundle(fresh);
      });
    }
  };

  const ensureFullCatalog = () => {
    requestCatalogLoad({ full: true });
  };

  /** Retry button on the catalog degraded state — reset + re-attempt, never a full page reload. */
  const retryCatalogLoad = () => {
    resetCatalogV2Cache();
    catalogLoadRequestedRef.current = false;
    setLoadError(false);
    requestCatalogLoad();
  };

  // AP3 — after Manager publish, same TMA return must observe new local-products projection (Store SKU).
  useEffect(() => {
    let lastAt = 0;
    const refreshLocalProjection = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastAt < 1500) return;
      lastAt = now;
      invalidateCustomerLocalProductsCache();
      resetCatalogV2Cache();
      catalogLoadRequestedRef.current = false;
      requestCatalogLoad({ full: true });
    };
    const onVis = () => {
      if (document.visibilityState === "visible") refreshLocalProjection();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", refreshLocalProjection);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", refreshLocalProjection);
    };
  }, []);

  const enterPostOrderSuccess = (
    orderId: string,
    status: string | undefined,
    displayNumber: string | undefined,
    paymentMethod: PaymentMethodChoice,
    meta?: {
      totalUah?: number;
      contactName?: string;
      contactPhone?: string;
      bankPayment?: BankPaymentClientPayload | null;
      bankSheetOpen?: boolean;
      paymentTiming?: "immediate" | "after_manager_confirmation";
      autoBankHandoff?: boolean;
    },
  ) => {
    persistPaymentResume({
      orderId,
      displayCode: displayNumber ?? null,
      provider: paymentMethod,
      originRoute: "checkout",
      flowState: "order_created_unpaid",
    });
    emitEvent("order_created", {
      order_id: orderId,
      display_order_number: displayNumber ?? null,
      payment_method: paymentMethod,
    });
    if (consumeReferredFirstOrderSlot()) {
      emitEvent("referred_user_first_order", {
        payment_method: paymentMethod,
      });
    }
    emitEvent("recipe_sent_planned", { order_id: orderId, enabled: false });
    // STAGING-LAUNCH-READINESS-002-R2-R2 — an unpaid NovaPay order must not empty the
    // cart: the customer may abandon the hosted payment page and come back to retry.
    // The cart is cleared only once payment is confirmed paid (see the lifecycle effect
    // below), when the customer explicitly clears it, or when the order is cancelled.
    // FOP / COD keep the previous immediate-clear behaviour (no live online session to lose).
    if (paymentMethod !== "novapay_card") {
      setCart(clearCart());
      setPromoCodeState(null);
      persistPromoCode(null);
    }
    setBankConfirmedPaid(false);
    bankConfirmedRef.current = false;
    setBankPayload(meta?.bankPayment ?? null);
    setBankSheetOpen(
      paymentMethod === "bank_transfer_fop" &&
        meta?.bankSheetOpen === true &&
        meta?.paymentTiming !== "after_manager_confirmation",
    );
    setStack([
      {
        name: "success",
        orderId,
        displayNumber,
        status,
        paymentMethod,
        totalUah: meta?.totalUah,
        contactName: meta?.contactName,
        contactPhone: meta?.contactPhone,
        bankPayment: meta?.bankPayment ?? null,
        bankSheetOpen:
          meta?.bankSheetOpen === true && meta?.paymentTiming !== "after_manager_confirmation",
        paymentTiming: meta?.paymentTiming ?? "immediate",
        autoBankHandoff:
          meta?.autoBankHandoff === true && meta?.paymentTiming !== "after_manager_confirmation",
      },
    ]);
  };

  useEffect(() => {
    try {
      document.documentElement.classList.add("sv2-html");
      document.body.classList.add("sv2-body-scroll");
      const root = document.getElementById("root");
      root?.classList.add("sv2-mount");
      applyStoreV2Theme(theme);
    } catch {
      /* ignore */
    }
    return () => {
      try {
        document.documentElement.classList.remove("sv2-html");
        document.body.classList.remove("sv2-body-scroll");
        document.getElementById("root")?.classList.remove("sv2-mount");
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    applyStoreV2Theme(theme);
    writeStoreV2Theme(theme);
  }, [theme]);

  useEffect(() => {
    initEvents({ sessionKey, buildId: STORE_V2_BUILD_ID });
    const launchStart = readLaunchStartParam();
    const dispatched = dispatchStartParam(launchStart);
    // TELEGRAM-GROWTH-R1 — capture trusted startapp attribution (never blocks boot).
    void attributeGrowthStartapp(sessionKey, launchStart || null, telegramUserId());

    const referralCode = readReferralCodeFromLaunch();
    if (referralCode) {
      void recordReferralClick(sessionKey, referralCode);
      const start =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("tgWebAppStartParam") ??
            new URLSearchParams(window.location.search).get("start_param") ??
            `ref_${referralCode}`
          : `ref_${referralCode}`;
      void attributeReferralStartapp(sessionKey, start, telegramUserId()).then((ok) => {
        if (!ok) return;
        markReferredSession();
        emitEvent("referral_deeplink_opened", { via: "startapp" });
        emitEvent("referred_user_first_open", { via: "startapp" });
      });
    }

    // STAGING-LAUNCH-READINESS-002-R2 — Telegram "📦 Мої замовлення" button opens the
    // Mini App with start_param=orders; wire it straight into the cabinet orders list.
    // TELEGRAM-GROWTH-R1 — shop* must land on store home (not cabinet).
    const cabinetStartParam = launchStart.toLowerCase();
    if (cabinetStartParam === "orders" && !readPayretStartParam() && dispatched.kind === "orders") {
      setStack(stackForRoute({ name: "orders" }) as typeof stack);
    }
    if (dispatched.kind === "shop" || (dispatched.route_store_home && dispatched.kind === "campaign")) {
      // Explicit store home — do not open cabinet from advertising links.
      setStack([{ name: "home" }]);
    }

    let cancelled = false;
    (async () => {
      try {
        // AP3R3R1G1 — shell first when not on payret/payment-resume critical path.
        const payretCritical =
          Boolean(readPayretStartParam()) || hasActivePaymentResume();
        if (!payretCritical && !cancelled) {
          setBootstrapping(false);
        }
        const boot = await fetchCustomerBootstrap(sessionKey);
        if (cancelled) return;
        if (boot) {
          setCustomerBootstrap(boot);
          const mktState = String(boot.marketing_permission?.state ?? "UNKNOWN").toUpperCase();
          if (
            mktState === "GRANTED" ||
            mktState === "WITHDRAWN" ||
            mktState === "DENIED" ||
            mktState === "UNKNOWN"
          ) {
            setMarketingPermissionState(mktState);
            setMarketingSuppressed(mktState === "WITHDRAWN" || mktState === "DENIED");
          }
          const handshake = enforceCustomerRuntimeHandshake(boot);
          if (handshake === "redirecting") return;
          if (handshake === "blocked") {
            setRuntimeRevBlocked(true);
            setConsentAccepted(false);
            return;
          }
          if (boot.consent.required || qaConsentReset || wasStagingQaConsentResetThisSession()) {
            clearMutableFirstRunCustomerState();
            applyBootstrapToLocalCache(boot);
            setConsentAccepted(false);
          } else {
            applyBootstrapToLocalCache(boot);
            persistConsent(lang);
            setConsentAccepted(true);
          }
        } else {
          setConsentAccepted(false);
        }
        const startParam = readPayretStartParam();
        const needServer =
          Boolean(startParam) && boot != null && boot.consent.required !== true;
        if (needServer) {
          const restored = await restorePayretContext({ startParam });
          if (cancelled) return;
          if (restored.order?.order_id) {
            const paid =
              restored.route === "paid_dashboard" || restored.payment?.status === "paid";
            const method = (restored.order.payment_method ?? "novapay_card") as PaymentMethodChoice;
            const openDocuments = isDocumentsStartParam(startParam);
            const reminderLaunch = isPaymentReminderStartParam(startParam);
            if (reminderLaunch.continuePayment) {
              emitEvent("payment_reminder_continue_payment", { order_id_present: true });
            }
            if (reminderLaunch.openOrder) {
              emitEvent("payment_reminder_open_order", { order_id_present: true });
            }
            // COMMERCE-FISCAL-161A-R1 — payret deep link opens order detail (receipt section scrolls into view).
            // CUSTOMER-R4.15 — docsret_ opens order detail Documents section (not Home).
            if (
              openDocuments ||
              reminderLaunch.openOrder ||
              (paid && String(startParam ?? "").toLowerCase().startsWith("payret"))
            ) {
              setStack([
                { name: "home" },
                { name: "orders" },
                { name: "order-detail", orderId: restored.order.order_id },
              ]);
              if (openDocuments) {
                emitEvent("document_deeplink_opened", {
                  destination: "documents",
                  start_param_kind: "docsret",
                  order_id_present: true,
                });
                window.setTimeout(() => {
                  document
                    .querySelector('[data-testid="sv2-documents-section"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 400);
              }
            } else {
              setStack([
                {
                  name: "success",
                  orderId: restored.order.order_id,
                  displayNumber: restored.order.display_order_number,
                  status: paid ? "paid" : restored.payment?.status ?? "pending",
                  paymentMethod: method,
                  totalUah: restored.order.total_uah ?? undefined,
                  contactName: restored.order.contact_name ?? undefined,
                  contactPhone: restored.order.contact_phone ?? undefined,
                },
              ]);
            }
            if (!paid) {
              persistPaymentResume({
                orderId: restored.order.order_id,
                displayCode: restored.order.display_order_number,
                provider: method,
                flowState: "payment_checking",
              });
            }
            if (restored.payment?.status) setNovapayLifecycleStatus(restored.payment.status);
            persistSuccessView({
              orderId: restored.order.order_id,
              displayNumber: restored.order.display_order_number,
              status: paid ? "paid" : restored.payment?.status ?? "pending",
              paymentMethod: restored.order.payment_method ?? "novapay_card",
              ...(restored.order.total_uah != null ? { totalUah: restored.order.total_uah } : {}),
              ...(restored.order.contact_name ? { contactName: restored.order.contact_name } : {}),
              ...(restored.order.contact_phone ? { contactPhone: restored.order.contact_phone } : {}),
            });
          } else if (startParam) {
            // payret without server order — fall back to same-WebView persisted success only.
            const saved = readPersistedSuccessView();
            if (saved?.orderId) {
              setStack([
                {
                  name: "success",
                  orderId: saved.orderId,
                  displayNumber: saved.displayNumber,
                  status: saved.status,
                  paymentMethod: saved.paymentMethod,
                  totalUah: saved.totalUah,
                  contactName: saved.contactName,
                  contactPhone: saved.contactPhone,
                },
              ]);
            }
          }
        } else if (readPayretStartParam()) {
          const saved = readPersistedSuccessView();
          if (saved?.orderId) {
            setStack([
              {
                name: "success",
                orderId: saved.orderId,
                displayNumber: saved.displayNumber,
                status: saved.status,
                paymentMethod: saved.paymentMethod,
                totalUah: saved.totalUah,
                contactName: saved.contactName,
                contactPhone: saved.contactPhone,
              },
            ]);
          }
        }

      } catch {
        setConsentAccepted(false);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    void (async () => {
      if (cancelled) return;
      if (!hasActivePaymentResume()) return;
      const resume = readPaymentResume();
      if (!resume) return;
      patchPaymentResumeFlowState("payment_checking");
      if (resume.provider === "novapay_card") {
        const res = await getNovapayPaymentStatusV2({ orderId: resume.order_id, sessionKey });
        if (cancelled) return;
        if (res.ok && res.payment?.status) {
          setNovapayLifecycleStatus(res.payment.status);
          if (res.payment.status === "paid") {
            patchPaymentResumeFlowState("payment_paid_confirmed");
            clearPaymentResume();
          }
        }
      }
      if (!cancelled) setBootstrapping(false);
    })();

    setCart(getCart());
    setPromoCodeState(getPromoCode());
    emitStagingLaunchDiagnostics(STORE_V2_BUILD_ID);
    return () => {
      cancelled = true;
    };
  }, [sessionKey]);

  useEffect(() => {
    if (!consentAccepted) return;
    if (hasActivePaymentResume()) return;
    requestCatalogLoad();
  }, [consentAccepted]);

  useEffect(() => {
    if (!consentAccepted || !homeMeta) return;
    const idle = window.setTimeout(() => {
      prefetchNewArrivalsPage1();
      prefetchBrandIndexV2();
      prefetchCountryIndexV2();
      for (const id of POPULAR_CATEGORY_PREFETCH_IDS) {
        prefetchCategoryPage(id, resolveCategorySortBeforeFetch(id));
      }
    }, 400);
    return () => window.clearTimeout(idle);
  }, [consentAccepted, homeMeta]);

  useEffect(() => {
    if (!consentAccepted) return;
    void loadBrandIndexV2().then((rows) => {
      if (rows.length) setBrandIndex(mapBrandIndexRows(rows));
    });
    void loadCountryIndexV2().then((rows) => {
      if (rows.length) setCountryIndex(mapCountryIndexRows(rows));
    });
  }, [consentAccepted]);

  useEffect(() => {
    if (!consentAccepted) return;
    void persistLegalConsentRemote({
      sessionKey,
      locale: lang,
      buildId: STORE_V2_BUILD_ID,
    });
    // Fire-and-forget for customers who already accepted locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, [consentAccepted, sessionKey]);

  // AP3R3R1G1 — absolute fail: blank shell must not exceed 8s (was 12s).
  useEffect(() => {
    if (!bootstrapping) return;
    const failsafe = window.setTimeout(() => setBootstrapping(false), 8_000);
    return () => window.clearTimeout(failsafe);
  }, [bootstrapping]);

  const view: View = stack[stack.length - 1] ?? { name: "home" };

  const routeKey =
    view.name === "category"
      ? `${view.name}:${view.categoryId}`
      : view.name === "search"
        ? `${view.name}:${view.query}`
        : view.name === "product"
          ? `${view.name}:${view.productId}`
          : view.name === "brand"
            ? `${view.name}:${view.slug}`
          : view.name === "success"
            ? `${view.name}:${view.orderId}`
            : view.name === "order-detail"
              ? `${view.name}:${view.orderId}`
              : view.name === "country"
                ? `${view.name}:${view.countryId}`
              : view.name;

  useEffect(() => {
    const publicStg =
      view.name === "success"
        ? view.displayNumber ?? null
        : null;
    setObservabilityRoute({
      route: view.name,
      sourceRoute: lastRouteKeyRef.current,
      publicOrderNumber: publicStg,
      productId: view.name === "product" ? view.productId : null,
      categoryId: view.name === "category" ? view.categoryId : null,
      brandId: view.name === "brand" ? view.slug : null,
      countryId: view.name === "country" ? view.countryId : null,
      searchQuery: view.name === "search" ? view.query : null,
      sort: view.name === "category" ? categoryBrowse?.sort ?? null : null,
    });
    bumpNavigationId();
    const started = performance.now();
    emitEvent("page_view", { route: view.name });
    emitEvent("route_load_started", { route: view.name });
    return () => {
      const ms = Math.round(performance.now() - started);
      recordRouteLoadDuration(ms);
      emitEvent("route_load_completed", { route: view.name, duration_ms: ms });
      emitEvent("page_leave", { route: view.name, duration_ms: ms });
    };
  }, [routeKey]);

  useEffect(() => {
    if (bootstrapping) return;
    if (bootTelemetrySentRef.current) return;
    bootTelemetrySentRef.current = true;
    setObservabilityBuildId(STORE_V2_BUILD_ID);
    emitEvent(consentAccepted ? "customer_boot_completed" : "customer_boot_failed", {
      consent: consentAccepted,
    });
    if (consentAccepted) {
      const apiBase =
        (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ||
        "https://asiastyle-workers-api.yaroslavcook1.workers.dev";
      const scheduleAttestation = () => {
        void recordCustomerProductionBootAttestation(apiBase);
      };
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(scheduleAttestation, { timeout: 5000 });
      } else {
        window.setTimeout(scheduleAttestation, 1500);
      }
    }
  }, [bootstrapping, consentAccepted]);

  useEffect(() => {
    if (bootstrapping || !consentAccepted) return;
    void syncCartRecoveryToServer().then(() =>
      fetchActiveRecovery().then((r) => {
        if (r?.ok) setRecoveryPayload(r);
      }),
    );
  }, [bootstrapping, consentAccepted, cart.length]);

  useEffect(() => {
    if (bootstrapping || !consentAccepted) {
      setHomeMarketingOptInVisible(false);
      return;
    }
    const show = shouldShowHomeMarketingOptIn({
      permissionState: marketingPermissionState,
      sessionKey,
    });
    setHomeMarketingOptInVisible(show);
    if (show) {
      const mem = readHomeMarketingOptInMemory();
      writeHomeMarketingOptInMemory({ ...mem, last_shown_session: sessionKey });
      emitEvent("marketing_optin_home_shown", { permission_state: marketingPermissionState });
    }
  }, [bootstrapping, consentAccepted, marketingPermissionState, sessionKey]);

  useEffect(() => {
    if (bootstrapping || consentAccepted) return;
    markEntryMarketingOptionSeen(sessionKey);
    emitEvent("legal_gate_views", { consent_version: CONSENT_VERSION });
    emitEvent("marketing_option_views", { surface: "entry_legal_gate" });
  }, [bootstrapping, consentAccepted, sessionKey]);

  useEffect(() => {
    if (bootstrapping || !consentAccepted) return;
    emitEvent("store_entries", { permission_state: marketingPermissionState });
  }, [bootstrapping, consentAccepted]);

  useEffect(() => {
    const root = document.querySelector("[data-store-scroll-root='true']");
    const scrollEl = root instanceof HTMLElement ? root : null;
    const onScroll = () => {
      const top = scrollEl ? scrollEl.scrollTop : window.scrollY;
      const max = scrollEl
        ? scrollEl.scrollHeight - scrollEl.clientHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const bucket = noteScrollDepthBucket((top / max) * 100);
      if (bucket != null) emitEvent("scroll_depth", { bucket, route: view.name });
    };
    const target: EventTarget = scrollEl ?? window;
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [routeKey, view.name]);

  useEffect(() => {
    if (view.name === "new" && newArrivalsBrowse == null) reloadNewArrivals();
  }, [view.name, newArrivalsBrowse]);

  // CUSTOMER-R4.9 / R4.15-C — first paint of category products (incremental or full).
  useEffect(() => {
    if (view.name !== "category") return;
    const browse =
      categoryBrowse?.categoryId === view.categoryId && categoryBrowse.products.length > 0
        ? categoryBrowse
        : null;
    if (browse) {
      emitEvent("category_first_content", {
        category_key: view.categoryId,
        product_count: browse.products.length,
        total_count: browse.total,
        incremental: true,
      });
      return;
    }
    if (!bundle) return;
    emitEvent("category_first_content", {
      category_key: view.categoryId,
      product_count: productsForCategory(bundle, view.categoryId).length,
      incremental: false,
    });
  }, [view, bundle, categoryBrowse]);

  useEffect(() => {
    // FUNCTIONALITY-115C / K11 route scroll contract:
    // forward → top; back → restore (handled in back()); home-return → restore session Home Y when policy allows.
    const intent = navIntentRef.current;
    const routeChanged = lastRouteKeyRef.current !== routeKey;
    lastRouteKeyRef.current = routeKey;
    if (intent === "back" || intent === "restore") {
      navIntentRef.current = "idle";
      return;
    }
    if (intent === "forward" || intent === "home" || routeChanged) {
      scrollStoreToTop(`route:${routeKey}:${intent}`);
      navIntentRef.current = "idle";
    }
  }, [routeKey, stack.length]);

  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending || pending.routeKey !== routeKey) return;
    const listReady =
      view.name === "home" ||
      (view.name === "category" &&
        Boolean(categoryBrowse && categoryBrowse.categoryId === view.categoryId && categoryBrowse.products.length > 0)) ||
      (view.name === "brand" && Boolean(brandBrowse && brandBrowse.slug === view.slug && brandBrowse.products.length > 0)) ||
      (view.name === "country" &&
        Boolean(countryBrowse && countryBrowse.countryId === view.countryId && countryBrowse.products.length > 0)) ||
      (view.name === "new" && Boolean(newArrivalsBrowse && newArrivalsBrowse.products.length > 0)) ||
      (view.name === "search" && serverSearchHits.length > 0) ||
      (view.name === "brands" && brandIndex.length > 0) ||
      (view.name === "countries" && countryIndex.length > 0);
    if (!listReady) return;
    restoreStoreScroll(pending.top);
    pendingRestoreRef.current = null;
  }, [
    routeKey,
    view,
    categoryBrowse,
    brandBrowse,
    countryBrowse,
    newArrivalsBrowse,
    serverSearchHits.length,
    brandIndex.length,
    countryIndex.length,
  ]);

  // COMMERCE-LAUNCH-168D2 — owner-only IME forensic screen (?ime_debug=1 + allowlist).
  useEffect(() => {
    if (!isOwnerImeDebugAllowed()) return;
    setStack((prev) => (prev.some((v) => v.name === "ime-debug") ? prev : [{ name: "ime-debug" }]));
  }, []);

  useEffect(() => {
    if (view.name !== "success") {
      disarmPostOrderCloseGuard();
      return;
    }
    // R5A — never treat sticky payment_return as confirming without return_expected for this order.
    scrubStalePaymentReturnQuery(view.orderId);
    try {
      const still = new URLSearchParams(window.location.search).get("payment_return");
      if (still !== "success" && still !== "fail") setPaymentReturn(null);
    } catch {
      /* ignore */
    }
    armPostOrderCloseGuard();
    persistSuccessView({
      orderId: view.orderId,
      ...(view.displayNumber ? { displayNumber: view.displayNumber } : {}),
      ...(view.status ? { status: view.status } : {}),
      ...(view.paymentMethod ? { paymentMethod: view.paymentMethod } : {}),
      ...(view.totalUah != null ? { totalUah: view.totalUah } : {}),
      ...(view.contactName ? { contactName: view.contactName } : {}),
      ...(view.contactPhone ? { contactPhone: view.contactPhone } : {}),
    });
    void createReferralLink(sessionKey, view.orderId).then((created) => {
      if (created) {
        setReferral(created);
        emitEvent("referral_link_created", { code: created.code, order_id: view.orderId });
      }
    });
    return () => {
      disarmPostOrderCloseGuard();
    };
  }, [view.name === "success" ? view.orderId : "", sessionKey, view.name]);

  useEffect(() => {
    if (view.name !== "success" || view.paymentMethod !== "bank_transfer_fop") return;
    let cancelled = false;
    const orderId = view.orderId;
    const publicNumber = view.displayNumber ?? null;
    if (view.bankPayment && !bankPayload) setBankPayload(view.bankPayment);
    if (view.bankSheetOpen) setBankSheetOpen(true);

    const pollPaid = async (fromReturn: boolean) => {
      if (cancelled) return;
      if (fromReturn) {
        emitEvent("bank_returned_to_miniapp", { public_order_number: publicNumber });
      }
      const result = await pollAsiaStyleBankPaymentStatus({ sessionKey, orderId });
      if (cancelled) return;
      if (result.paid && !bankConfirmedRef.current) {
        bankConfirmedRef.current = true;
        emitEvent("bank_confirmed", { public_order_number: publicNumber });
        const reorder = peekReorderSession();
        if (reorder && publicNumber && reorder.newPublicOrderNumber === publicNumber) {
          emitEvent("reorder_paid", {
            source_public_order_number: reorder.sourcePublicOrderNumber,
            new_public_order_number: publicNumber,
            source_order_status: reorder.sourceOrderStatus,
            source_payment_status: reorder.sourcePaymentStatus,
          });
        }
        if (isReferredSession()) {
          emitEvent("referred_user_paid_order", { public_order_number: publicNumber });
        }
        setBankConfirmedPaid(true);
        setBankSheetOpen(false);
      }
      return result.paid;
    };

    const timers: number[] = [];
    void (async () => {
      if (!bankPayload) {
        const loaded = await fetchBankInstructionV2({ sessionKey, orderId });
        if (!cancelled && loaded) setBankPayload(loaded);
      }
      const paid = await pollPaid(false);
      if (cancelled || paid || bankConfirmedRef.current) return;
      for (const delay of BANK_RESUME_POLL_MS) {
        timers.push(
          window.setTimeout(() => {
            void pollPaid(false);
          }, delay),
        );
      }
    })();

    const onVis = () => {
      if (document.visibilityState === "visible") {
        setBankSheetOpen(false);
        void pollPaid(true);
      }
    };
    const onResume = () => {
      setBankSheetOpen(false);
      void pollPaid(true);
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
      for (const id of timers) window.clearTimeout(id);
    };
  }, [view.name === "success" ? view.orderId : "", view.name === "success" ? view.paymentMethod : "", sessionKey]);

  // MANAGER-ORDERS-141-R2 — ensure referral payload exists when reopening an order in cabinet.
  useEffect(() => {
    if (view.name !== "order-detail") return;
    void createReferralLink(sessionKey, view.orderId).then((created) => {
      if (created) setReferral(created);
    });
  }, [view.name === "order-detail" ? view.orderId : "", sessionKey, view.name]);

  // STAGING-LAUNCH-READINESS-002-R2-R2 — cart vs order: an unpaid NovaPay order is
  // never allowed to empty the cart (see enterPostOrderSuccess). Clear it here, once,
  // the moment the payment lifecycle actually confirms "paid" for that order.
  useEffect(() => {
    if (view.name !== "success" || view.paymentMethod !== "novapay_card") return;
    if (novapayLifecycleStatus !== "paid") return;
    if (novapayCartClearedForOrderRef.current === view.orderId) return;
    novapayCartClearedForOrderRef.current = view.orderId;
    setCart(clearCart());
    setPromoCodeState(null);
    persistPromoCode(null);
  }, [view.name === "success" ? view.orderId : "", view.name === "success" ? view.paymentMethod : "", novapayLifecycleStatus]);

  // R7 — resume Mini App after NovaPay: bounded backoff poll; never mark paid from browser return.
  useEffect(() => {
    if (view.name !== "success" || view.paymentMethod !== "novapay_card") return;
    const orderId = view.orderId;
    const applyStatus = (status: string) => {
      setNovapayLifecycleStatus(status);
      if (status === "paid") {
        setStack((prev) => {
          const top = prev[prev.length - 1];
          if (!top || top.name !== "success" || top.orderId !== orderId || top.status === "paid") {
            return prev;
          }
          return [...prev.slice(0, -1), { ...top, status: "paid" }];
        });
        patchPaymentResumeFlowState("payment_paid_confirmed");
        clearPaymentResume();
      }
    };
    const refresh = () => {
      void getNovapayPaymentStatusV2({ orderId, sessionKey }).then((res) => {
        if (res.ok && res.payment?.status) applyStatus(res.payment.status);
      });
    };
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onPageShow = () => refresh();
    const cancelBackoff = scheduleBoundedRetries(refresh);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      cancelBackoff();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [
    view.name === "success" ? view.orderId : "",
    view.name === "success" ? view.paymentMethod : "",
    sessionKey,
  ]);

  // R2 — NovaPay WebView resume: stay on order payment surface (no catalog flash).
  // STAGING-LAUNCH-READINESS-002-R2-R2 — root cause of the reported "Мої замовлення →
  // Назад hang": this listener used to force-navigate back to the pending-payment
  // success screen on EVERY visibilitychange (e.g. switching Telegram chats and back)
  // as long as a payment resume was still active in sessionStorage — even after the
  // customer had explicitly navigated to Orders/Home/anywhere else. `userNavigatedRef`
  // (set by push/back/navigateHomeNow) makes this a one-shot: it only fires for a
  // genuine "just returned from the external NovaPay tab" case, before the customer
  // has touched in-app navigation at all.
  useEffect(() => {
    const resumeToOrder = () => {
      if (userNavigatedRef.current) return;
      const resume = readPaymentResume();
      if (!resume) return;
      patchPaymentResumeFlowState("payment_checking");
      setBootstrapping(true);
      setStack([
        {
          name: "success",
          orderId: resume.order_id,
          displayNumber: resume.display_code ?? undefined,
          paymentMethod: resume.provider,
          status: "pending",
        },
      ]);
      void getNovapayPaymentStatusV2({ orderId: resume.order_id, sessionKey })
        .then((res) => {
          if (res.ok && res.payment?.status) {
            setNovapayLifecycleStatus(res.payment.status);
            if (res.payment.status === "paid") {
              patchPaymentResumeFlowState("payment_paid_confirmed");
              clearPaymentResume();
            }
          }
        })
        .finally(() => setBootstrapping(false));
    };
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      resumeToOrder();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [sessionKey]);

  const push = (v: View) => {
    userNavigatedRef.current = true;
    setContactToast(null);
    navIntentRef.current = "forward";
    const currentScroll = readStoreScrollTop();
    if (view.name === "home") {
      homeScrollSessionRef.current = currentScroll;
      writeSessionHomeScroll(currentScroll);
    }
    setScrollMemory((memory) => {
      const next = memory.slice(0, Math.max(0, stack.length));
      next[stack.length - 1] = currentScroll;
      next.push(0);
      return next;
    });
    setStack((s) => [...s, v]);
    emitEvent("navigation_click", {
      target_key: `nav.${v.name}`,
      from: view.name,
      to: v.name,
    });
  };
  const back = () => {
    userNavigatedRef.current = true;
    setContactToast(null);
    navIntentRef.current = "back";
    setStack((s) => {
      if (s.length <= 1) {
        setScrollMemory([0]);
        navIntentRef.current = "home";
        return [{ name: "home" }];
      }
      const next = s.slice(0, -1);
      const landingView = next[next.length - 1] ?? ({ name: "home" } as const);
      const landing = landingView.name;
      const restoreTop = scrollMemory[next.length - 1] ?? 0;
      setScrollMemory((memory) => memory.slice(0, next.length));
      if (shouldRestoreBackScroll(landing) && restoreTop > 0) {
        const landingKey =
          landingView.name === "category"
            ? `category:${landingView.categoryId}`
            : landingView.name === "search"
              ? `search:${landingView.query}`
              : landingView.name === "brand"
                ? `brand:${landingView.slug}`
                : landingView.name === "country"
                  ? `country:${landingView.countryId}`
                  : landingView.name === "product"
                    ? `product:${landingView.productId}`
                    : landing;
        pendingRestoreRef.current = { routeKey: landingKey, top: restoreTop };
        restoreStoreScroll(restoreTop);
      } else if (!shouldRestoreBackScroll(landing)) {
        pendingRestoreRef.current = null;
        scrollStoreToTop("back_fresh");
      }
      return next;
    });
  };
  const navigateHomeNow = () => {
    userNavigatedRef.current = true;
    setContactToast(null);
    clearPersistedSuccessView();
    clearPaymentResume();
    const pending = peekReorderSession();
    if (isActiveReorderSession(pending) && pending) {
      emitEvent("reorder_abandoned", {
        ...reorderAnalyticsBase(pending),
        item_count_added: pending.itemCountAdded,
        item_count_unavailable: pending.itemCountUnavailable,
        cart_value_current: pending.cartValueCurrent,
      });
      patchReorderSession({ abandoned: true });
    }
    if (view.name === "home") {
      hideTelegramBackButton();
      return;
    }
    const restoreHome = shouldRestoreHomeScroll(view.name);
    const savedHome = Math.max(homeScrollSessionRef.current, readSessionHomeScroll());
    hideTelegramBackButton();
    disarmPostOrderCloseGuard();
    if (restoreHome && savedHome > 0) {
      navIntentRef.current = "restore";
      pendingRestoreRef.current = { routeKey: "home", top: savedHome };
      setScrollMemory([savedHome]);
      setStack([{ name: "home" }]);
      restoreStoreScroll(savedHome);
    } else {
      navIntentRef.current = "home";
      pendingRestoreRef.current = null;
      if (!restoreHome) {
        homeScrollSessionRef.current = 0;
        clearSessionHomeScroll();
      }
      setScrollMemory([0]);
      setStack([{ name: "home" }]);
      scrollStoreToTop("home_button");
    }
    requestCatalogLoad();
  };

  /**
   * STAGING-LAUNCH-READINESS-002-R2-R2 — root cause of "back/home may no-op" on an
   * unpaid NovaPay success screen: nativeBackRef/home/chromeBack used to just
   * `return` (no navigation at all) whenever the order was still unpaid, trapping
   * the customer on that screen. Back/Home must always land somewhere usable —
   * Orders (cabinet) for Back, Home for the explicit Home action — without
   * requiring the catalog bundle first (cabinet routes never gate on it).
   */
  const navigateAwayFromPendingPayment = (target: "orders" | "home") => {
    userNavigatedRef.current = true;
    setContactToast(null);
    clearPersistedSuccessView();
    clearPaymentResume();
    navIntentRef.current = "home";
    pendingRestoreRef.current = null;
    homeScrollSessionRef.current = 0;
    clearSessionHomeScroll();
    setScrollMemory([0]);
    setStack(target === "orders" ? (stackForRoute({ name: "orders" }) as typeof stack) : [{ name: "home" }]);
    if (target === "home") hideTelegramBackButton();
    disarmPostOrderCloseGuard();
    scrollStoreToTop(`unpaid_exit_${target}`);
    requestCatalogLoad();
  };

  const finishCloseStore = () => {
    // CUSTOMER-MINIAPP-131 — armed close via store wrapper (= closeMiniApp Telegram path).
    if (closeStoreExplicitly() === "browser_fallback") {
      setBrowserExitOpen(true);
      showToast(lang === "en" ? STORE_V2_BROWSER_CLOSE_HINT_EN : STORE_V2_BROWSER_CLOSE_HINT_UA);
    }
  };

  const onBrowserCloseFallback = () => {
    setBrowserExitOpen(true);
    clearDirectBrowserContinueAllowed();
    setDirectBrowserOk(false);
  };

  const tryReferralExitRescue = (proceed: () => void): boolean => {
    if (view.name !== "success") {
      proceed();
      return false;
    }
    const paid =
      view.paymentMethod === "novapay_card"
        ? novapayLifecycleStatus === "paid"
        : view.paymentMethod === "bank_transfer_fop"
          ? fopReviewActive === false && view.status === "paid"
          : false; // COD / unknown — referral reward stays pending until eligibility event
    const alreadyShared = wasReferralSharedForOrder(view.orderId);
    if (
      !canShowReferralExitRescue({
        orderId: view.orderId,
        paid,
        alreadyShared,
      })
    ) {
      proceed();
      return false;
    }
    exitRescuePendingRef.current = proceed;
    markReferralExitShown(view.orderId);
    setExitRescueOpen(true);
    return true;
  };

  const nativeBackRef = useRef<() => void>(() => {});
  const goLogicalParentRef = useRef<(route: string) => void>(() => {});
  nativeBackRef.current = () => {
    if (successMethodSheetOpen) {
      setSuccessMethodSheetOpen(false);
      setSuccessMethodSaveError(null);
      return;
    }
    if (view.name === "success") {
      const unpaidNovapay =
        view.paymentMethod === "novapay_card" && novapayLifecycleStatus !== "paid" && view.status !== "paid";
      if (unpaidNovapay) {
        tryReferralExitRescue(() => navigateAwayFromPendingPayment("orders"));
        return;
      }
      tryReferralExitRescue(navigateHomeNow);
      return;
    }
    back();
  };

  const stackNavKey = useMemo(() => {
    const top = stack[stack.length - 1];
    return `${stack.length}:${top?.name ?? "?"}`;
  }, [stack]);

  useEffect(() => {
    return mountTelegramBackController({
      stack,
      onDismissOverlay: () => {
        if (successMethodSheetOpen) {
          setSuccessMethodSheetOpen(false);
          setSuccessMethodSaveError(null);
          return true;
        }
        return false;
      },
      onStackBack: () => nativeBackRef.current(),
      onLogicalParent: (route) => goLogicalParentRef.current(route),
    });
  }, [stackNavKey, stack, successMethodSheetOpen]);

  const home = () => {
    if (view.name === "success") {
      const unpaidNovapay =
        view.paymentMethod === "novapay_card" && novapayLifecycleStatus !== "paid" && view.status !== "paid";
      if (unpaidNovapay) {
        tryReferralExitRescue(() => navigateAwayFromPendingPayment("home"));
        return;
      }
      tryReferralExitRescue(navigateHomeNow);
      return;
    }
    navigateHomeNow();
  };

  const openCategory = (categoryId: string, sortRaw?: CatalogSortKey | string) => {
    const sort = sortRaw
      ? normalizeCatalogSortKey(sortRaw)
      : resolveCategorySortBeforeFetch(categoryId);
    const filters = "";
    const page = 1;
    const queryKey = categoryProductsQueryKey({ categoryId, sort, filters, page });
    emitEvent("category_load_started", {
      category_key: categoryId,
      warm: Boolean(bundle),
      mode: "incremental",
      sort,
      query_key: queryKey,
    });
    emitEvent("category_view", { category_key: categoryId });
    emitEvent("category_open", { category_key: categoryId });
    emitEvent("category_viewed", { category_key: categoryId, sort });
    emitEvent("filter_applied", { filter_type: "category", filter_value: categoryId });
    const cached = peekCategoryPageCache(categoryId, 1, sort);
    const gen = ++categoryLoadGenRef.current;
    setCategoryBrowse({
      categoryId,
      sort,
      products: cached?.products ?? [],
      page: cached?.page ?? 0,
      total: cached?.total ?? 0,
      hasMore: cached?.hasMore ?? true,
      loading: !cached,
      loadingMore: false,
      error: null,
    });
    push({ name: "category", categoryId });
    void loadCategoryPage(categoryId, 1, sort)
      .then((pageResult) => {
        if (gen !== categoryLoadGenRef.current) return;
        for (const p of pageResult.products) {
          if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
        }
        emitEvent("category_products_painted", {
          query_key: queryKey,
          first_ids: firstPaintProductIds(pageResult.products),
        });
        const sameIds =
          cached &&
          firstPaintProductIds(cached.products).join("|") ===
            firstPaintProductIds(pageResult.products).join("|");
        if (sameIds && cached.total === pageResult.total) return;
        setCategoryBrowse({
          categoryId,
          sort,
          products: pageResult.products,
          page: pageResult.page,
          total: pageResult.total,
          hasMore: pageResult.hasMore,
          loading: false,
          loadingMore: false,
          error: null,
        });
        emitEvent("category_loaded", {
          category_key: categoryId,
          product_count: pageResult.products.length,
          total_count: pageResult.total,
          page: pageResult.page,
          sort,
          incremental: true,
          query_key: queryKey,
        });
      })
      .catch((err) => {
        if (gen !== categoryLoadGenRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (cached) {
          setCategoryBrowse((prev) => (prev ? { ...prev, loading: false } : prev));
          return;
        }
        setCategoryBrowse({
          categoryId,
          sort,
          products: [],
          page: 0,
          total: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: err instanceof Error ? err.message : "category_fetch_failed",
        });
        emitEvent("category_load_failed", { reason: "category_page_fetch", category_key: categoryId, sort });
      });
  };

  const changeCategorySort = (sortRaw: CatalogSortKey | string) => {
    if (!categoryBrowse) return;
    const sort = normalizeCatalogSortKey(sortRaw);
    if (sort === categoryBrowse.sort) return;
    const categoryId = categoryBrowse.categoryId;
    emitEvent("category_sort_changed", { category_key: categoryId, sort, previous_sort: categoryBrowse.sort });
    const cached = peekCategoryPageCache(categoryId, 1, sort);
    const gen = ++categoryLoadGenRef.current;
    setCategoryBrowse({
      categoryId,
      sort,
      products: cached?.products ?? [],
      page: cached?.page ?? 0,
      total: cached?.total ?? categoryBrowse.total,
      hasMore: cached?.hasMore ?? true,
      loading: !cached,
      loadingMore: false,
      error: null,
    });
    void loadCategoryPage(categoryId, 1, sort)
      .then((page) => {
        if (gen !== categoryLoadGenRef.current) return;
        for (const p of page.products) {
          if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
        }
        emitEvent("category_products_painted", {
          query_key: categoryProductsQueryKey({ categoryId, sort, filters: "", page: 1 }),
          first_ids: firstPaintProductIds(page.products),
        });
        setCategoryBrowse({
          categoryId,
          sort,
          products: page.products,
          page: page.page,
          total: page.total,
          hasMore: page.hasMore,
          loading: false,
          loadingMore: false,
          error: null,
        });
      })
      .catch((err) => {
        if (gen !== categoryLoadGenRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setCategoryBrowse((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : "category_sort_fetch_failed",
              }
            : prev,
        );
      });
  };

  const loadMoreCategory = () => {
    if (!categoryBrowse || categoryBrowse.loadingMore || !categoryBrowse.hasMore) return;
    const categoryId = categoryBrowse.categoryId;
    const sort = categoryBrowse.sort;
    const nextPage = categoryBrowse.page + 1;
    const gen = categoryLoadGenRef.current;
    const signal = categoryAbortRef.current?.signal;
    setCategoryBrowse((prev) => (prev ? { ...prev, loadingMore: true, error: null } : prev));
    void loadCategoryPage(categoryId, nextPage, sort, signal)
      .then((page) => {
        if (gen !== categoryLoadGenRef.current) return;
        setCategoryBrowse((prev) => {
          if (!prev || prev.categoryId !== categoryId || prev.sort !== sort) return prev;
          const seen = new Set(prev.products.map((p) => p.id));
          const appended = page.products.filter((p) => !seen.has(p.id));
          for (const p of appended) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          return {
            ...prev,
            products: [...prev.products, ...appended],
            page: page.page,
            total: page.total,
            hasMore: page.hasMore,
            loadingMore: false,
            error: null,
          };
        });
      })
      .catch(() => {
        if (gen !== categoryLoadGenRef.current) return;
        setCategoryBrowse((prev) =>
          prev
            ? {
                ...prev,
                loadingMore: false,
                error: prev.products.length > 0 ? null : "category_page_fetch_failed",
              }
            : prev,
        );
      });
  };

  const openBrand = (slug: string, source = "brand_directory") => {
    const id = String(slug ?? "").trim();
    if (!id) return;
    emitEvent("brand_opened", { brand_id: id, source });
    emitEvent("brand_open", { brand_id: id, source });
    emitEvent("filter_applied", { filter_type: "brand", filter_value: id });
    const sort: CatalogSortKey = "recommended";
    const cached = peekBrandPageCache(id, 1, sort);
    const gen = ++brandLoadGenRef.current;
    setBrandBrowse({
      slug: id,
      products: cached?.products ?? [],
      page: cached?.page ?? 0,
      total: cached?.total ?? 0,
      hasMore: cached?.hasMore ?? true,
      loading: !cached,
      loadingMore: false,
      error: null,
      sort,
      localComplete: false,
    });
    push({ name: "brand", slug: id });
    void loadBrandPage(id, 1, sort)
      .then((pageResult) => {
        if (gen !== brandLoadGenRef.current) return;
        for (const p of pageResult.products) {
          if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
        }
        setBrandBrowse({
          slug: id,
          products: pageResult.products,
          page: pageResult.page,
          total: pageResult.total,
          hasMore: pageResult.hasMore,
          loading: false,
          loadingMore: false,
          error: null,
          sort,
          localComplete: false,
        });
      })
      .catch((err) => {
        if (gen !== brandLoadGenRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (cached) {
          setBrandBrowse((prev) => (prev ? { ...prev, loading: false } : prev));
          return;
        }
        setBrandBrowse({
          slug: id,
          products: [],
          page: 0,
          total: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: err instanceof Error ? err.message : "brand_fetch_failed",
          sort,
          localComplete: false,
        });
      });
  };

  const changeBrandSort = (sortRaw: CatalogSortKey | string) => {
    if (!brandBrowse) return;
    const sort = normalizeCatalogSortKey(sortRaw);
    if (sort === brandBrowse.sort && !brandBrowse.localComplete) return;
    const slug = brandBrowse.slug;
    const gen = ++brandLoadGenRef.current;
    setBrandBrowse((prev) => (prev ? { ...prev, sort, loading: true, error: null } : prev));
    void loadBrandPage(slug, 1, sort)
      .then(async (pageResult) => {
        if (gen !== brandLoadGenRef.current) return;
        const shardSort = normalizeCatalogSortKey(pageResult.sort);
        if (shardSort === sort) {
          for (const p of pageResult.products) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          setBrandBrowse({
            slug,
            products: pageResult.products,
            page: pageResult.page,
            total: pageResult.total,
            hasMore: pageResult.hasMore,
            loading: false,
            loadingMore: false,
            error: null,
            sort,
            localComplete: false,
          });
          return;
        }
        setBrandBrowse((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: "sort_index_unavailable",
                localComplete: false,
              }
            : prev,
        );
      })
      .catch((err) => {
        if (gen !== brandLoadGenRef.current) return;
        setBrandBrowse((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : "brand_sort_failed",
              }
            : prev,
        );
      });
  };

  const loadMoreBrand = () => {
    if (!brandBrowse || brandBrowse.loadingMore || !brandBrowse.hasMore || brandBrowse.localComplete) return;
    const slug = brandBrowse.slug;
    const sort = brandBrowse.sort;
    const nextPage = brandBrowse.page + 1;
    const gen = brandLoadGenRef.current;
    setBrandBrowse((prev) => (prev ? { ...prev, loadingMore: true, error: null } : prev));
    void loadBrandPage(slug, nextPage, sort)
      .then((page) => {
        if (gen !== brandLoadGenRef.current) return;
        setBrandBrowse((prev) => {
          if (!prev || prev.slug !== slug || prev.sort !== sort) return prev;
          const seen = new Set(prev.products.map((p) => p.id));
          const appended = page.products.filter((p) => !seen.has(p.id));
          for (const p of appended) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          return {
            ...prev,
            products: [...prev.products, ...appended],
            page: page.page,
            total: page.total,
            hasMore: page.hasMore,
            loadingMore: false,
            error: null,
          };
        });
      })
      .catch(() => {
        if (gen !== brandLoadGenRef.current) return;
        setBrandBrowse((prev) =>
          prev
            ? {
                ...prev,
                loadingMore: false,
                error: prev.products.length > 0 ? null : "brand_page_fetch_failed",
              }
            : prev,
        );
      });
  };

  const openCountry = (countryId: string, source = "country_directory") => {
    const id = String(countryId ?? "").trim();
    if (!id) return;
    emitEvent("country_opened", { country_id: id, meaning: "cuisine_country", source });
    emitEvent("country_open", { country_id: id, source });
    emitEvent("filter_applied", {
      filter_type: "cuisine_country",
      filter_value: id,
      meaning: "cuisine_country",
    });
    const sort: CatalogSortKey = "recommended";
    const cached = peekCountryPageCache(id, 1, sort);
    const gen = ++countryLoadGenRef.current;
    setCountryBrowse({
      countryId: id,
      products: cached?.products ?? [],
      page: cached?.page ?? 0,
      total: cached?.total ?? 0,
      hasMore: cached?.hasMore ?? true,
      loading: !cached,
      loadingMore: false,
      error: null,
      sort,
      localComplete: false,
    });
    push({ name: "country", countryId: id });
    void loadCountryPage(id, 1, sort)
      .then((pageResult) => {
        if (gen !== countryLoadGenRef.current) return;
        for (const p of pageResult.products) {
          if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
        }
        setCountryBrowse({
          countryId: id,
          products: pageResult.products,
          page: pageResult.page,
          total: pageResult.total,
          hasMore: pageResult.hasMore,
          loading: false,
          loadingMore: false,
          error: null,
          sort,
          localComplete: false,
        });
      })
      .catch((err) => {
        if (gen !== countryLoadGenRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (cached) {
          setCountryBrowse((prev) => (prev ? { ...prev, loading: false } : prev));
          return;
        }
        setCountryBrowse({
          countryId: id,
          products: [],
          page: 0,
          total: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: err instanceof Error ? err.message : "country_fetch_failed",
          sort,
          localComplete: false,
        });
      });
  };

  const changeCountrySort = (sortRaw: CatalogSortKey | string) => {
    if (!countryBrowse) return;
    const countryId = countryBrowse.countryId;
    const sort = normalizeCatalogSortKey(sortRaw);
    if (sort === countryBrowse.sort && !countryBrowse.localComplete) return;
    countryLoadGenRef.current += 1;
    const gen = countryLoadGenRef.current;
    setCountryBrowse((prev) => (prev ? { ...prev, sort, loading: true, error: null } : prev));
    void loadCountryPage(countryId, 1, sort)
      .then(async (pageResult) => {
        if (gen !== countryLoadGenRef.current) return;
        const shardSort = normalizeCatalogSortKey(pageResult.sort);
        if (shardSort === sort) {
          for (const p of pageResult.products) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          setCountryBrowse({
            countryId,
            products: pageResult.products,
            page: pageResult.page,
            total: pageResult.total,
            hasMore: pageResult.hasMore,
            loading: false,
            loadingMore: false,
            error: null,
            sort,
            localComplete: false,
          });
          return;
        }
        setCountryBrowse((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: "sort_index_unavailable",
                localComplete: false,
              }
            : prev,
        );
      })
      .catch((err) => {
        if (gen !== countryLoadGenRef.current) return;
        setCountryBrowse((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: err instanceof Error ? err.message : "country_sort_failed",
              }
            : prev,
        );
      });
  };

  const loadMoreCountry = () => {
    if (!countryBrowse || countryBrowse.loadingMore || !countryBrowse.hasMore || countryBrowse.localComplete) return;
    const countryId = countryBrowse.countryId;
    const sort = countryBrowse.sort;
    const nextPage = countryBrowse.page + 1;
    const gen = countryLoadGenRef.current;
    setCountryBrowse((prev) => (prev ? { ...prev, loadingMore: true, error: null } : prev));
    void loadCountryPage(countryId, nextPage, sort)
      .then((page) => {
        if (gen !== countryLoadGenRef.current) return;
        setCountryBrowse((prev) => {
          if (!prev || prev.countryId !== countryId || prev.sort !== sort) return prev;
          const seen = new Set(prev.products.map((p) => p.id));
          const appended = page.products.filter((p) => !seen.has(p.id));
          for (const p of appended) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          return {
            ...prev,
            products: [...prev.products, ...appended],
            page: page.page,
            total: page.total,
            hasMore: page.hasMore,
            loadingMore: false,
            error: null,
          };
        });
      })
      .catch(() => {
        if (gen !== countryLoadGenRef.current) return;
        setCountryBrowse((prev) =>
          prev
            ? {
                ...prev,
                loadingMore: false,
                error: prev.products.length > 0 ? null : "country_page_fetch_failed",
              }
            : prev,
        );
      });
  };

  const reloadNewArrivals = (sortRaw: CatalogSortKey | string = "newest") => {
    const sort = normalizeCatalogSortKey(sortRaw) === "recommended" ? "newest" : normalizeCatalogSortKey(sortRaw);
    emitEvent("category_load_started", { category_key: "new_arrivals", warm: Boolean(bundle), mode: "incremental" });
    const gen = ++newArrivalsLoadGenRef.current;
    setNewArrivalsBrowse({
      products: [],
      page: 0,
      total: 0,
      hasMore: true,
      loading: true,
      loadingMore: false,
      error: null,
      sort,
      localComplete: false,
    });
    void loadNewArrivalsPage(1, sort)
      .then(async (page) => {
        if (gen !== newArrivalsLoadGenRef.current) return;
        const shardSort = normalizeCatalogSortKey(page.sort ?? "newest");
        if (!page.shardSortMismatch && shardSort === sort) {
          for (const p of page.products) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          setNewArrivalsBrowse({
            products: page.products,
            page: page.page,
            total: page.total,
            hasMore: page.hasMore,
            loading: false,
            loadingMore: false,
            error: null,
            sort,
            localComplete: false,
          });
          emitEvent("category_loaded", {
            category_key: "new_arrivals",
            product_count: page.products.length,
            total_count: page.total,
            page: page.page,
            incremental: true,
          });
          return;
        }
        setNewArrivalsBrowse({
          products: [],
          page: 0,
          total: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: "sort_index_unavailable",
          sort,
          localComplete: false,
        });
      })
      .catch((err) => {
        if (gen !== newArrivalsLoadGenRef.current) return;
        setNewArrivalsBrowse({
          products: [],
          page: 0,
          total: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: err instanceof Error ? err.message : "new_arrivals_fetch_failed",
          sort,
          localComplete: false,
        });
      });
  };
  const openNewArrivals = () => {
    push({ name: "new" });
    reloadNewArrivals("newest");
  };
  const changeNewArrivalsSort = (sortRaw: CatalogSortKey | string) => {
    if (!newArrivalsBrowse) return;
    const sort = normalizeCatalogSortKey(sortRaw) === "recommended" ? "newest" : normalizeCatalogSortKey(sortRaw);
    if (sort === newArrivalsBrowse.sort && !newArrivalsBrowse.localComplete) return;
    reloadNewArrivals(sort);
  };
  const loadMoreNewArrivals = () => {
    if (!newArrivalsBrowse || newArrivalsBrowse.loadingMore || !newArrivalsBrowse.hasMore || newArrivalsBrowse.localComplete) return;
    const sort = newArrivalsBrowse.sort;
    const nextPage = newArrivalsBrowse.page + 1;
    const gen = newArrivalsLoadGenRef.current;
    setNewArrivalsBrowse((prev) => (prev ? { ...prev, loadingMore: true, error: null } : prev));
    void loadNewArrivalsPage(nextPage, sort)
      .then((page) => {
        if (gen !== newArrivalsLoadGenRef.current) return;
        setNewArrivalsBrowse((prev) => {
          if (!prev || prev.sort !== sort) return prev;
          const seen = new Set(prev.products.map((p) => p.id));
          const appended = page.products.filter((p) => !seen.has(p.id));
          for (const p of appended) {
            if (!productShellByIdRef.current.has(p.id)) productShellByIdRef.current.set(p.id, p);
          }
          return {
            ...prev,
            products: [...prev.products, ...appended],
            page: page.page,
            total: page.total,
            hasMore: page.hasMore,
            loadingMore: false,
            error: null,
          };
        });
      })
      .catch(() => {
        if (gen !== newArrivalsLoadGenRef.current) return;
        setNewArrivalsBrowse((prev) => (prev ? { ...prev, loadingMore: false } : prev));
      });
  };

  /** CUSTOMER-R4.15 — GLOBAL_CATALOG only; never filters by new arrivals / category / brand. */
  const openSearch = (query: string) => {
    // COMMERCE-LAUNCH-168D6 / R4A — bottom Search starts a NEW session (empty draft).
    if (!String(query ?? "").trim()) {
      searchInflightRef.current.abort?.abort();
      searchInflightRef.current = { id: searchInflightRef.current.id + 1, abort: null };
      const epoch = beginNewSearchSession();
      setSearchBarEpoch(epoch);
      setSuggestCloseToken((n) => n + 1);
      setServerSearchHits([]);
      setServerSearchMeta(null);
      if (view.name === "search") {
        // Leave results; go home with fresh search bar.
        setStack((s) => [...s.filter((v) => v.name !== "search"), { name: "home" }]);
      } else if (view.name !== "home") {
        setStack((s) => [...s, { name: "home" }]);
      }
      window.setTimeout(() => {
        try {
          document
            .querySelector<HTMLInputElement>('[data-isolated-search="1"] input[type="search"]')
            ?.focus({ preventScroll: true });
        } catch {
          /* ignore */
        }
      }, 50);
      return;
    }
    // Canonical query contract: analytics normalization must be the SAME
    // normalizer the search engine itself uses (foldToken), not a separate
    // ad-hoc lowercase — otherwise analytics and retrieval can disagree on
    // what the "normalized" query even is.
    commitSubmittedQuery(query);
    emitEvent("search_started", {
      original_query: query,
      normalized_query: foldToken(query),
      search_version: "server_search_index_v1",
      search_origin: view.name,
    });
    emitEvent("search_query_submitted", {
      original_query: query,
      normalized_query: foldToken(query),
      search_scope: "GLOBAL_CATALOG",
      search_origin: view.name,
    });
    emitEvent("search_submit", {
      original_query: query,
      search_scope: "GLOBAL_CATALOG",
    });
    emitEvent("search_scope_resolved", {
      search_scope: "GLOBAL_CATALOG",
      search_origin: view.name,
      search_version: "server_search_index_v1",
    });
    emitEvent("search_performed", {
      original_query: query,
      normalized_query: foldToken(query),
      search_version: "server_search_index_v1",
      source_route: view.name,
    });
    emitEvent("search", { query });

    searchInflightRef.current.abort?.abort();
    const ac = new AbortController();
    const requestId = searchInflightRef.current.id + 1;
    searchInflightRef.current = { id: requestId, abort: ac };

    setServerSearchMeta({
      query,
      correction: null,
      correctionKind: null,
      didYouMean: null,
      total: 0,
      catalogRevision: null,
      pending: true,
      error: false,
      requestId,
    });
    setSearchSlowPending(false);
    setServerSearchHits([]);
    push({ name: "search", query });
    const slowTimer = window.setTimeout(() => {
      if (searchInflightRef.current.id === requestId) setSearchSlowPending(true);
    }, 200);
    void serverSearch(query, { limit: 24, offset: 0, signal: ac.signal })
      .then((res) => {
        if (searchInflightRef.current.id !== requestId) return;
        window.clearTimeout(slowTimer);
        setSearchSlowPending(false);
        if (res.technical_error || res.outcome === "TEMPORARY_TECHNICAL_ERROR" || !res.ok) {
          setServerSearchHits([]);
          setServerSearchMeta({
            query,
            correction: null,
            correctionKind: null,
            didYouMean: null,
            total: 0,
            catalogRevision: null,
            pending: false,
            error: true,
            requestId,
          });
          emitEvent("search_failed", {
            original_query: query,
            normalized_query: foldToken(query),
            search_version: "server_search_index_v1",
            search_scope: "GLOBAL_CATALOG",
            search_origin: view.name,
            reason: res.error ?? "server_search_not_ok",
            technical_error: true,
          });
          return;
        }
        const hits = res.hits ?? [];
        setServerSearchHits(hits);
        setServerSearchMeta({
          query,
          correction: res.correction ?? null,
          correctionKind: res.correction_kind ?? null,
          didYouMean: res.did_you_mean ?? null,
          total: res.total ?? hits.length,
          catalogRevision: res.catalog_revision ?? null,
          pending: false,
          error: false,
          requestId,
        });
        markSearchPerf("search_results_received");
        const resolved = res.correction ?? query;
        const kind = res.correction_kind ?? null;
        const payload = {
          raw_normalized_query: foldToken(query),
          resolved_query: resolved,
          result_count: res.total ?? hits.length,
          correction_strategy: kind,
          origin: "search",
        };
        if (kind === "typo_correction" && res.correction) {
          emitEvent("search_typo_corrected", payload);
        } else if (kind === "typo_suggestion" || res.did_you_mean) {
          emitEvent("search_typo_suggestion", {
            ...payload,
            suggestion: res.did_you_mean ?? res.correction,
          });
        } else if ((res.total ?? hits.length) > 0) {
          emitEvent("search_exact_results", payload);
        }
      })
      .catch((err: unknown) => {
        if (searchInflightRef.current.id !== requestId) return;
        window.clearTimeout(slowTimer);
        setSearchSlowPending(false);
        const name = err && typeof err === "object" && "name" in err ? String((err as { name: string }).name) : "";
        if (name === "AbortError") return;
        setServerSearchHits([]);
        setServerSearchMeta({
          query,
          correction: null,
          correctionKind: null,
          didYouMean: null,
          total: 0,
          catalogRevision: null,
          pending: false,
          error: true,
          requestId,
        });
        emitEvent("search_failed", {
          original_query: query,
          normalized_query: foldToken(query),
          search_version: "server_search_index_v1",
          search_scope: "GLOBAL_CATALOG",
          search_origin: view.name,
          reason: "network_or_throw",
          technical_error: true,
        });
      });
  };
  const openProduct = (id: string, seed?: CatalogV2Product | { id?: string; title?: string; imageUrl?: string | null; priceUah?: number | null }) => {
    resetPdpPerf();
    markPdpPerf("product_tap_ts");
    let resolved = seed && "categoryKeys" in seed ? (seed as CatalogV2Product) : undefined;
    if (!resolved) {
      resolved = resolveCatalogProductByAnyId(id, productById, productShellByIdRef.current);
    }
    if (!resolved) {
      const hit = serverSearchHits.find((h) => h.id === id);
      if (hit) resolved = hitToCatalogProduct(hit);
    }
    if (!resolved && seed && "title" in seed) {
      resolved = hitToCatalogProduct({
        id,
        title: String(seed.title ?? ""),
        brand: null,
        thumbnail: seed.imageUrl ?? null,
        availability: "in_stock",
        price: typeof seed.priceUah === "number" ? seed.priceUah : null,
        match_reason: "card_seed",
        type: "product",
      });
    }
    if (!resolved) resolved = productById.get(id) ?? productShellByIdRef.current.get(id);
    if (resolved) {
      productShellByIdRef.current.set(resolved.id, resolved);
      if (resolved.id !== id) productShellByIdRef.current.set(id, resolved);
    }
    const routeId = resolved?.id ?? id;
    emitEvent("product_view", { product_id: routeId });
    emitEvent("product_open", { product_id: routeId });
    recordRecentProduct(routeId);
    markPdpPerf("pdp_route_ts");
    push({ name: "product", productId: routeId });
  };
  const prefetchProduct = (seed: CatalogV2Product) => {
    productShellByIdRef.current.set(seed.id, seed);
  };
  const registerActionableBatch = (products: CatalogV2Product[]) => {
    registerActionableProducts(productShellByIdRef.current, products);
  };
  const openRecommendationProduct = (product: CatalogV2Product) => {
    openProduct(product.id, product);
  };
  const openCart = () => push({ name: "cart" });
  const customerNav = useMemo(
    () =>
      createCustomerNavigationService({
        setStack: (next) => {
          userNavigatedRef.current = true;
          setContactToast(null);
          navIntentRef.current = "forward";
          setScrollMemory([0]);
          setStack(typeof next === "function" ? (prev) => next(prev as never) as typeof prev : (next as typeof stack));
        },
        emit: (name, detail) => emitEvent(name as never, detail),
      }),
    [],
  );
  goLogicalParentRef.current = (route) => customerNav.goLogicalParent(route);
  const openProfile = () => {
    customerNav.goProfile();
  };
  const openOrders = () => {
    customerNav.goOrders();
  };
  const openMessages = () => {
    customerNav.goMessages();
  };
  const openOrderDetail = (orderRef: string) => {
    customerNav.goOrder(normalizeCustomerOrderRef(orderRef));
  };
  const openEngagementHub = () => push({ name: "engagement-hub" });
  const openInviteFriends = () => {
    customerNav.goInviteFriends();
  };
  const requestExitSheet = () => {
    // Unblock × when payment method sheet is open (sheet z-index 1100 < close 1300,
    // but confirm path must not stay covered by a stale sheet).
    setSuccessMethodSheetOpen(false);
    setExitSheetOpen(true);
  };
  const handleRepeatOrder = (source: ReorderSourceInput) => {
    const sourceStg = String(source.order.display_order_number ?? "").trim();
    emitEvent("reorder_clicked", {
      source_public_order_number: sourceStg,
      source_order_status: source.order.order_top_status ?? source.order.status,
      source_payment_status: source.order.payment_status,
      item_count_source: source.items.length,
    });
    const op = beginReorderCartOperation(sourceStg);
    userNavigatedRef.current = true;
    clearPaymentResume();
    const goCart = () => {
      setScrollMemory([0, 0]);
      setStack([{ name: "home" }, { name: "cart" }]);
    };
    if (!op.apply) {
      goCart();
      return;
    }
    const run = async () => {
      ensureFullCatalog();
      let products = bundle?.products ?? [];
      try {
        const loaded = await loadCatalogV2();
        products = loaded.products;
        setBundle(loaded);
        setHomeMeta(loaded.meta);
      } catch {
        /* catalog miss → unavailable items, still leave the original order */
      }
      const snapshot = buildReorderSnapshot(source, products, titleOf);
      emitEvent("reorder_snapshot_loaded", {
        ...reorderAnalyticsBase(snapshot),
        item_count_added: snapshot.available.length,
        item_count_unavailable: snapshot.unavailable.length,
        cart_value_current: snapshot.cartValueCurrent,
      });
      for (const row of snapshot.available) {
        emitEvent("reorder_item_available", {
          source_public_order_number: snapshot.sourcePublicOrderNumber,
          item_count_added: 1,
        });
      }
      for (const _row of snapshot.unavailable) {
        emitEvent("reorder_item_unavailable", {
          source_public_order_number: snapshot.sourcePublicOrderNumber,
          item_count_unavailable: 1,
        });
      }
      const existing = getCart();
      const merged = mergeReorderIntoCart(existing, snapshot.available);
      // CUSTOMER-R4.9 — strip any leaked payment/order identity fields before persist.
      const sanitized = merged.map((row) => {
        const copy = { ...row } as CartV2Item & Record<string, unknown>;
        delete copy.payment_status;
        delete copy.payment_confirmed_bank_iban;
        delete copy.bank_confirmed;
        delete copy.public_order_number;
        delete copy.order_id;
        delete copy.paid_at;
        delete copy.fiscal_status;
        delete copy.receipt_id;
        return copy as CartV2Item;
      });
      const nextCart = writeCart(sanitized);
      setCart(nextCart);
      clearPaymentResume();
      clearPersistedSuccessView();
      const session = buildReorderSessionFromSnapshot(snapshot, existing.length > 0);
      writeReorderSession(session);
      if (session.cartHadItemsBefore && session.itemCountAdded > 0) {
        emitEvent("reorder_cart_merged", {
          ...reorderAnalyticsBase(snapshot),
          item_count_added: session.itemCountAdded,
          item_count_unavailable: session.itemCountUnavailable,
          cart_value_current: session.cartValueCurrent,
        });
      }
      const prev = readCheckoutPrefill();
      writeCheckoutPrefill({
        name: prev?.name ?? "",
        surname: prev?.surname ?? "",
        phone: prev?.phone ?? "",
        city: snapshot.deliveryCity?.trim() || prev?.city || "",
        warehouse: snapshot.deliveryWarehouse?.trim() || prev?.warehouse || "",
        note: "",
        ...(prev?.np_selection ? { np_selection: prev.np_selection } : {}),
        ...(prev?.email ? { email: prev.email } : {}),
        ...(prev?.birthday ? { birthday: prev.birthday } : {}),
      });
      showToast(
        session.itemCountAdded === 0
          ? t(lang, "reorder_item_unavailable")
          : session.cartHadItemsBefore
            ? t(lang, "reorder_cart_merged")
            : t(lang, "reorder_cart_added", { STG: snapshot.sourcePublicOrderNumber }),
      );
      goCart();
    };
    void run();
  };
  const openLegalFromSource = (panel: LegalHubPanel, source: LegalNavSource) => {
    setLegalNavContext({
      source,
      returnScrollTop: readStoreScrollTop(),
      skipHubOnDocumentBack: legalSourceSkipsHub(source),
      checkoutStep: view.name === "checkout" ? "checkout" : null,
    });
    setProfileLegalPanel(panel);
    push({ name: "profile" });
  };
  const openLegalDocument = (panel: LegalHubPanel) => {
    openLegalFromSource(panel, "legal_hub");
  };
  const returnFromLegalDocument = () => {
    const ctx = peekLegalNavContext();
    const scroll = ctx?.returnScrollTop ?? 0;
    clearLegalNavContext();
    back();
    if (scroll > 0) {
      restoreStoreScroll(scroll);
    }
  };
  const openCheckout = () => {
    const session = peekReorderSession();
    if (isActiveReorderSession(session) && session) {
      patchReorderSession({ checkoutOpened: true });
      emitEvent("reorder_checkout_opened", {
        ...reorderAnalyticsBase(session),
        item_count_added: session.itemCountAdded,
        item_count_unavailable: session.itemCountUnavailable,
        cart_value_current: session.cartValueCurrent,
      });
    }
    emitEvent("checkout_start", { item_count: cart.reduce((s, i) => s + i.qty, 0) });
    emitEvent("checkout_started", { item_count: cart.reduce((s, i) => s + i.qty, 0) });
    void markCheckoutRecoveryProgress("CHECKOUT");
    push({ name: "checkout" });
  };

  const productById = useMemo(() => {
    const map = new Map<string, CatalogV2Product>();
    for (const p of bundle?.products ?? []) map.set(p.id, p);
    return map;
  }, [bundle]);

  const titleOf = useMemo(() => {
    return (p: CatalogV2Product) =>
      resolveProductTitle(
        {
          id: p.id,
          sku: p.sku,
          title: p.title,
          titleRu: p.titleRu,
          brand: p.brand,
          categoryName: p.categoryName,
          categoryKeys: p.categoryKeys,
        },
        lang,
      ).title;
  }, [lang]);

  const openAvailabilitySheet = (product: CatalogV2Product) => {
    setAvailabilityFeedback(null);
    setAvailabilitySheetProduct(product);
  };

  const submitAvailabilitySheet = (input: {
    phone: string;
    comment: string;
    attachment: AvailabilityAttachment | null;
    honeypot: string;
    interactionMs: number;
    idempotencyKey: string;
    challengeToken?: string | null;
  }) => {
    const product = availabilitySheetProduct;
    if (!product || availabilityBusy) return;
    setAvailabilityBusy(true);
    void requestProductAvailability({
      product,
      displayTitle: titleOf(product),
      lang,
      sessionKey,
      sourceRoute: routeKey,
      customerPhone: input.phone,
      customerComment: input.comment,
      customerName: lastCheckoutSnapshot
        ? `${lastCheckoutSnapshot.name} ${lastCheckoutSnapshot.surname}`.trim()
        : null,
      priceDisplay: product.displayPrice,
      weight: product.weight,
      attachment: input.attachment,
      honeypot: input.honeypot,
      interactionMs: input.interactionMs,
      idempotencyKey: input.idempotencyKey,
      challengeToken: input.challengeToken ?? null,
    })
      .then((result) => {
        setAvailabilityFeedback({ productId: product.id, message: result.message });
        if (result.ok) setAvailabilitySheetProduct(null);
      })
      .finally(() => setAvailabilityBusy(false));
  };

  const cartIdentity = cart.map((i) => i.id).join("|");

  const applyJourneyResult = (result: ReturnType<typeof getCartJourneyRecommendations>) => {
    for (const item of result.items) productShellByIdRef.current.set(item.product.id, item.product);
    setJourneyRecs(result.items);
    journeyRecCursorRef.current = result.rotationToken;
    setJourneyRecCanRefresh(result.poolSize > 3);
    setJourneyRecRotationIndex(result.rotationIndex);
  };

  useEffect(() => {
    if (cart.length === 0) {
      journeyRecLoadGenRef.current += 1;
      journeyRecPoolRef.current = [];
      journeyRecCursorRef.current = 0;
      setJourneyRecs([]);
      setJourneyRecCanRefresh(false);
      setJourneyRecLoading(false);
      setJourneyRecRefreshing(false);
      setJourneyRecRotationIndex(0);
      return;
    }
    const gen = ++journeyRecLoadGenRef.current;
    const cartIds = cart.map((i) => i.id);
    if (journeyRecPoolRef.current.length === 0) setJourneyRecLoading(true);
    const anchors = cartIds
      .map((id) => productById.get(id) ?? productShellByIdRef.current.get(id))
      .filter((p): p is CatalogV2Product => Boolean(p));
    void loadCartRecommendationPool(anchors)
      .then(async (pool) => {
        if (gen !== journeyRecLoadGenRef.current) return;
        for (const p of pool) productShellByIdRef.current.set(p.id, p);
        journeyRecPoolRef.current = pool;
        journeyRecCursorRef.current = 0;
        const onsite = await fetchOnsiteRecommendations({
          surface: "CART_CROSS_SELL",
          cartProductIds: cartIds,
          limit: 3,
        }).catch(() => null);
        if (
          onsite?.ok &&
          onsite.enabled &&
          !onsite.hidden &&
          onsite.items &&
          onsite.items.length > 0
        ) {
          const mapped = mapOnsiteToCrossSell(onsite.items, productById, lang).map((c, idx) => ({
            ...c,
            placement: "CART_CROSS_SELL" as const,
            strategy: "cross_sell_rules" as const,
            rank: idx + 1,
          }));
          if (mapped.length > 0) {
            for (const item of mapped) productShellByIdRef.current.set(item.product.id, item.product);
            setJourneyRecs(mapped);
            setJourneyRecCanRefresh(false);
            setJourneyRecRotationIndex(0);
            return;
          }
        }
        applyJourneyResult(
          getCartJourneyRecommendations({
            placement: "CART_CROSS_SELL",
            catalog: pool,
            cartProductIds: cartIds,
            lang,
            rotationCursor: 0,
            limit: 3,
          }),
        );
      })
      .catch(() => {
        /* keep existing visible recommendations */
      })
      .finally(() => {
        if (gen === journeyRecLoadGenRef.current) setJourneyRecLoading(false);
      });
    // productById is a cache of shells/catalog; cart identity is the load key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartIdentity, lang]);

  const refreshJourneyRecommendations = (placement: "CART_CROSS_SELL" | "CHECKOUT_CROSS_SELL") => {
    if (journeyRecRefreshLockRef.current) return;
    if (!journeyRecCanRefresh) return;
    journeyRecRefreshLockRef.current = true;
    setJourneyRecRefreshing(true);
    try {
      const currentIds = journeyRecs.map((r) => r.product.id).join("|");
      const result = getCartJourneyRecommendations({
        placement,
        catalog: journeyRecPoolRef.current,
        cartProductIds: cart.map((i) => i.id),
        lang,
        rotationCursor: journeyRecCursorRef.current,
        limit: 3,
      });
      const nextIds = result.items.map((r) => r.product.id).join("|");
      if (result.items.length > 0 && nextIds !== currentIds) {
        applyJourneyResult(result);
      }
      emitEvent("recommendation_refresh", {
        placement,
        strategy: result.strategy,
        rotation_index: result.rotationIndex,
        cart_product_ids: cart.map((i) => i.id),
      });
      emitEvent("cart_recommendation_refresh", { page: result.rotationIndex, placement });
      emitEvent("cta_click", { target_key: "recommendation.refresh", placement });
    } finally {
      journeyRecRefreshLockRef.current = false;
      setJourneyRecRefreshing(false);
    }
  };

  const cartRecommendations = journeyRecs;

  const cartCount = cartTotalQuantity(cart);

  // STAGING-LAUNCH-READINESS-002-R2-R7-P0 — never arm Telegram native closing
  // confirmation (browser/Telegram “Changes may not be saved”). Branded exit sheet only.
  const exitRetentionState = resolveExitRetentionState({ viewName: view.name, cartCount });
  useEffect(() => {
    syncExitClosingConfirmation(false);
  }, [exitRetentionState, view.name]);

  const exitSheetRecentProducts = useMemo(() => {
    return readRecentProductIds()
      .filter((id) => !(view.name === "product" && view.productId === id))
      .map((id) => productById.get(id))
      .filter((p): p is CatalogV2Product => Boolean(p))
      .slice(0, 6)
      .map((p) => ({ id: p.id, title: titleOf(p) }));
  }, [exitSheetOpen, productById, titleOf, view]);

  const handleAdd = (product: CatalogV2Product) => {
    if (!product.isSellable || product.priceUah == null) return;
    const priced = resolveCustomerVisiblePrice(product);
    if (!(priced.primary > 0)) return;
    const unit = resolveCustomerSellPrice(product);
    // STAGING-LAUNCH-READINESS-002-R2-R5 — cart line sku = store article only.
    // Never write Prom id, never write barcode into the store-SKU slot.
    const storeArticle =
      product.merchantSku?.trim() ||
      product.merchantArticle?.trim() ||
      null;
    setCart(
      addToCart({
        id: product.id,
        title: titleOf(product),
        priceUah: unit,
        imageUrl: product.imageUrl,
        sku: storeArticle && storeArticle !== product.id ? storeArticle : null,
        brand: product.brand,
        weight: product.weight,
        maxQty: product.maxOrderQty ?? null,
        stockStatus: product.stockStatus ?? "unknown",
        availabilityLabel:
          product.isSellable
            ? lang === "en"
              ? "In stock"
              : "В наявності"
            : lang === "en"
              ? "Unavailable"
              : "Немає в наявності",
      }),
    );
    emitEvent("add_to_cart", {
      product_id: product.id,
      price: priced.primary,
      promotion_active: priced.saleActive,
    });
    emitEvent("cta_click", { target_key: "product.add_to_cart", product_id: product.id });
    showToast(t(lang, "added_toast"));
  };

  const handleAddRecommendation = (
    productOrId: CatalogV2Product | string,
    surface: string = "CART_COMPLEMENT",
  ) => {
    void (async () => {
      const productId = typeof productOrId === "string" ? productOrId : productOrId.id;
      let product =
        (typeof productOrId !== "string" ? productOrId : null) ??
        resolveCatalogProductByAnyId(productId, productById, productShellByIdRef.current) ??
        productById.get(productId) ??
        productShellByIdRef.current.get(productId) ??
        journeyRecs.find((r) => r.product.id === productId)?.product ??
        null;
      if (product) productShellByIdRef.current.set(product.id, product);
      emitEvent("recommendation_add_attempt", {
        surface,
        product_id: productId,
        release: "R7.2.2F3",
      });
      const sellability = resolveCustomerSellability(product);
      if (product && sellabilityBlocksAdd(sellability)) {
        emitEvent("recommendation_add_failure", {
          surface,
          product_id: productId,
          failure_category: sellability.toLowerCase(),
          release: "R7.2.2F3",
        });
        showToast(sellabilityMessageUk(sellability));
        return;
      }
      let validationOk = true;
      if (product && sellability === "SELLABLE") {
        const validation = await validateOnsiteAdd(productId).catch(() => ({ ok: false as const }));
        if (!validation.ok) {
          // Fail-open when local shell still says sellable (missing stock_qty must not block).
          const retrySellability = resolveCustomerSellability(product);
          validationOk = retrySellability === "SELLABLE";
        }
      }
      if (!validationOk) {
        emitEvent("recommendation_add_failure", {
          surface,
          product_id: productId,
          failure_category: "unavailable",
          release: "R7.2.2F3",
        });
        showToast("Цей товар щойно став недоступним.");
        return;
      }
      if (!product) {
        emitEvent("recommendation_add_failure", {
          surface,
          product_id: productId,
          failure_category: "unresolved",
          release: "R7.2.2F3",
        });
        emitEvent("customer_route_unresolved", {
          surface,
          candidate_id_class: "recommendation",
          resolution_outcome: "unresolved",
          release: "R7.2.2F3",
        });
        showToast("Не вдалося додати товар. Спробуйте ще раз.");
        return;
      }
      productShellByIdRef.current.set(product.id, product);
      handleAdd(product);
      emitEvent("recommendation_add_success", {
        surface,
        product_id: product.id,
        release: "R7.2.2F3",
      });
      emitEvent("crosssell_add", { product_id: productId, source: "store_v2", channel: "onsite", release: "R7.1" });
      emitEvent("crosssell_added_to_order", { product_id: productId, source: "store_v2" });
      emitEvent("cart_recommendation_add", { product_id: productId, source: "store_v2", channel: "onsite" });
      emitEvent("recommendation_add_to_cart", {
        placement: surface,
        recommended_product_id: productId,
        rotation_index: journeyRecRotationIndex,
        cart_product_ids: cart.map((i) => i.id),
        channel: "onsite",
        release: "R7.1",
      });
      emitEvent("cta_click", { target_key: "recommendation.add", product_id: productId });
    })();
  };

  const handleAddCheckoutRecommendation = (productOrId: CatalogV2Product | string) => {
    const productId = typeof productOrId === "string" ? productOrId : productOrId.id;
    if (typeof productOrId !== "string") {
      productShellByIdRef.current.set(productOrId.id, productOrId);
    }
    const product =
      (typeof productOrId !== "string" ? productOrId : null) ??
      productById.get(productId) ??
      productShellByIdRef.current.get(productId) ??
      journeyRecs.find((r) => r.product.id === productId)?.product;
    if (!product) {
      showToast("Не вдалося додати товар. Спробуйте ще раз.");
      return;
    }
    handleAdd(product);
    emitEvent("checkout_recommendation_add", { product_id: productId, source: "checkout" });
    emitEvent("recommendation_add_to_cart", {
      placement: "CHECKOUT_CROSS_SELL",
      recommended_product_id: productId,
      rotation_index: journeyRecRotationIndex,
      cart_product_ids: cart.map((i) => i.id),
    });
    emitEvent("cta_click", { target_key: "recommendation.add", product_id: productId });
  };

  const handleInc = (id: string) => {
    setCart(incItem(id));
    emitEvent("cart_qty_change", { product_id: id, direction: "inc" });
    showToast(lang === "en" ? "Quantity updated" : "Кількість оновлено");
  };
  const handleDec = (id: string) => {
    const before = cart.find((i) => i.id === id)?.qty ?? 0;
    const next = decItem(id);
    setCart(next);
    emitEvent("cart_qty_change", { product_id: id, direction: "dec" });
    const after = next.find((i) => i.id === id)?.qty ?? 0;
    if (before > 0 && after === 0) showToast(lang === "en" ? "Removed from cart" : "Видалено з кошика");
    else showToast(lang === "en" ? "Quantity updated" : "Кількість оновлено");
  };

  const applyPromo = (raw: string) => {
    const code = normalizePromoCode(raw);
    setPromoCodeState(code || null);
    persistPromoCode(code || null);
    const subtotal = subtotalOf(cart);
    const ev = evaluatePromo(code, subtotal);
    // 103: hardened promo analytics — code / status / cart_total / discount / session / source.
    const payload = {
      code,
      status: ev.status,
      reason: ev.status,
      cart_total: subtotal,
      subtotal_uah: subtotal,
      discount: ev.discountUah,
      discount_uah: ev.discountUah,
      session_id: sessionKey,
      source: "store_v2",
    };
    emitEvent(ev.status === "applied" ? "promo_apply" : "promo_reject", payload);
  };
  const removePromo = () => {
    setPromoCodeState(null);
    persistPromoCode(null);
  };

  const acceptConsent = async (marketingOptIn = false) => {
    if (consentBusy) return;
    setConsentBusy(true);
    setConsentError(false);
    const remote = await persistLegalConsentRemote({
      sessionKey,
      locale: lang,
      buildId: STORE_V2_BUILD_ID,
      marketingOptIn: marketingOptIn === true,
    });
    if (!remote.ok) {
      setConsentBusy(false);
      setConsentError(true);
      return;
    }
    persistConsent(lang);
    clearStagingQaConsentResetSessionFlag();
    markEntryMarketingOptionSeen(sessionKey);
    emitEvent("consent_accept", {
      consent_version: CONSENT_VERSION,
      locale: lang,
      duplicate: remote.duplicate === true,
      marketing_opt_in: marketingOptIn === true,
      marketing_granted: remote.marketing?.granted === true,
    });
    if (isReferredSession()) {
      emitEvent("referred_user_consent", { consent_version: CONSENT_VERSION });
    }
    if (remote.marketing?.granted) {
      setMarketingSuppressed(false);
      setMarketingPermissionState("GRANTED");
    }
    const refreshed = await fetchCustomerBootstrap(sessionKey);
    if (refreshed?.consent.required) {
      setConsentAccepted(false);
      setConsentBusy(false);
      setConsentError(true);
      return;
    }
    if (refreshed) setCustomerBootstrap(refreshed);
    setConsentAccepted(true);
    setConsentBusy(false);
    if (!hasActivePaymentResume()) requestCatalogLoad();
  };

  const requestDeleteData = () => {
    emitEvent("customer_data_delete_requested", { session_id: sessionKey, consent_version: CONSENT_VERSION });
    setDeleteRequested(true);
    setMarketingSuppressed(true);
  };

  const requestMarketingOptOut = () => {
    emitEvent("customer_marketing_suppressed", { session_id: sessionKey, consent_version: CONSENT_VERSION });
    setMarketingSuppressed(true);
    setMarketingPermissionState("WITHDRAWN");
  };

  const requestMarketingOptIn = (source = "CUSTOMER_CABINET") => {
    emitEvent("customer_marketing_granted", {
      session_id: sessionKey,
      consent_version: CONSENT_VERSION,
      source,
      purpose: "MARKETING",
      channel: "TELEGRAM",
      copy_version:
        source === "HOME_CONTEXTUAL_CARD"
          ? HOME_MARKETING_COPY_VERSION_CLIENT
          : "ENTRY_MARKETING_OPTIN_R1_20260820",
    });
    setMarketingSuppressed(false);
    setMarketingPermissionState("GRANTED");
  };

  // FUNCTIONALITY-105 — hard reset of Store V2 WebView state (never touches D1 orders),
  // then reload the app fresh with a cache-bust so the WebView cannot keep poisoned state.
  const resetStoreCache = () => {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith("asiastyle.store-v2.")) toRemove.push(k);
      }
      for (const k of toRemove) window.localStorage.removeItem(k);
    } catch {
      /* storage unavailable */
    }
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("reset", "1");
      u.searchParams.set("cachebust", "115cr1");
      window.location.replace(u.toString());
    } catch {
      window.location.reload();
    }
  };

  // FUNCTIONALITY-115C-R1 — open Contact Manager modal (Phone/Viber/Telegram/Close).
  // NOVAPAY-116-R2 — do NOT notify manager on modal open; only on explicit channel click.
  const contactManager = (source: string, orderId?: string, extra?: Record<string, unknown>) => {
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number; username?: string } } } };
    }).Telegram?.WebApp?.initDataUnsafe?.user;
    const context = buildManagerContactContext({
      sourceSurface: source,
      sourceRoute: routeKey,
      locale: lang,
      telegramUserId: tg?.id != null ? String(tg.id) : null,
      username: tg?.username ?? null,
      orderId: orderId ?? null,
      productId: typeof extra?.product_id === "string" ? extra.product_id : null,
      sku: typeof extra?.sku === "string" ? extra.sku : null,
      productName: typeof extra?.title === "string" ? extra.title : null,
      brand: typeof extra?.brand === "string" ? extra.brand : null,
      category: typeof extra?.category === "string" ? extra.category : null,
      paymentMethod: typeof extra?.payment_method === "string" ? extra.payment_method : null,
      referralCode: typeof extra?.referral_code === "string" ? extra.referral_code : null,
    });
    if (typeof extra?.display_order_number === "string") {
      context.display_order_number = extra.display_order_number;
    }
    setContactContext(context);
    setContactModal(true);
  };

  const emitExplicitManagerContact = (channel: "phone" | "viber" | "telegram") => {
    if (!contactContext) return;
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number } } } };
    }).Telegram?.WebApp?.initDataUnsafe?.user;
    const dedupeKey = `${tg?.id ?? "anon"}:${String(contactContext.source_surface ?? "")}:${channel}:${routeKey}`;
    const now = Date.now();
    if (lastManagerContactKeyRef.current === dedupeKey && now - lastManagerContactAtRef.current <= 60_000) {
      return;
    }
    lastManagerContactKeyRef.current = dedupeKey;
    lastManagerContactAtRef.current = now;
    emitEvent("manager_contact_requested", {
      ...contactContext,
      selected_contact_channel: channel,
      event_id: `mgrctx-${now}`,
    });
  };

  // 104: badge shows the deploy build id (proves the fresh bundle loaded in WebView),
  // not the catalog snapshot's build id.
  const buildId = STORE_V2_BUILD_ID;

  // COMMERCE-LAUNCH-168D6 — searchableProducts only when full catalog already loaded
  // (PDP / category). Search suggest+submit use server index — never require this on Home.
  const searchableProducts = useMemo(() => {
    if (!bundle) return [] as SearchableProduct[];
    return bundle.products.map((p) => ({
      id: p.id,
      sku: p.sku,
      merchantSku: p.merchantSku,
      merchantArticle: p.merchantArticle,
      barcode: p.barcodeGtin,
      barcodeGtin: p.barcodeGtin,
      promProductId: p.promProductId || p.id,
      manufacturerPartNumber: p.manufacturerPartNumber,
      titleAliases: p.titleAliases,
      title: p.title,
      titleRu: p.titleRu,
      brand: p.brand,
      categoryName: p.categoryName,
      categoryKeys: p.categoryKeys,
      search: p.search,
      imageUrl: p.imageUrl,
      priceUah: p.priceUah,
    }));
  }, [bundle]);

  const catalogRevision = bundle?.meta.catalogRevision || homeMeta?.catalogRevision || buildId;

  // CUSTOMER-172A — NO client compact-index / IDB full-catalog warm on Home.
  // Server search artifact is built at catalog-sync time.

  // SEARCH-PERF-169A / R4A — home interactive once home surface can paint (meta or catalog).
  useEffect(() => {
    if (view.name === "home" && (bundle || homeMeta)) markSearchPerf("home_interactive");
  }, [view.name, bundle, homeMeta]);

  const [suggestCloseToken, setSuggestCloseToken] = useState(0);
  const [searchBarEpoch, setSearchBarEpoch] = useState(0);
  useEffect(() => {
    // Close overlay on route change (home → search results, etc.).
    setSuggestCloseToken((n) => n + 1);
  }, [view.name]);

  const searchOutcome = useMemo(() => {
    if (view.name !== "search") {
      return {
        products: [] as CatalogV2Product[],
        didYouMean: null as string | null,
        skuSuggestion: null as string | null,
        zeroPhrase: null as string | null,
        diagnostics: null as null,
        topHit: null as null,
        identifierCollisions: 0,
        pending: false,
        correction: null as string | null,
        correctionKind: null as string | null,
        requestId: 0,
      };
    }
    const products = serverSearchHits.map(hitToCatalogProduct);
    // Prefer full-catalog rows when already loaded (richer PDP identity).
    const enriched =
      bundle && products.length > 0
        ? products.map((p) => bundle.products.find((b) => b.id === p.id) ?? p)
        : products;
    const pending = serverSearchMeta?.pending === true;
    // SEARCH_FAILED ≠ empty: never paint nothing_found phrasing on network/API fail.
    const searchFailed = serverSearchMeta?.error === true;
    return {
      products: enriched,
      didYouMean: serverSearchMeta?.didYouMean ?? serverSearchMeta?.correction ?? null,
      skuSuggestion: null as string | null,
      zeroPhrase:
        !pending && !searchFailed && enriched.length === 0
          ? pickZeroResultPhrase(lang, view.query)
          : null,
      diagnostics: null,
      topHit: serverSearchHits[0]
        ? { id: serverSearchHits[0].id, matchReason: serverSearchHits[0].match_reason }
        : null,
      identifierCollisions: 0,
      pending,
      correction: serverSearchMeta?.correction ?? null,
      correctionKind: serverSearchMeta?.correctionKind ?? null,
      requestId: serverSearchMeta?.requestId ?? 0,
    };
  }, [view, serverSearchHits, serverSearchMeta, bundle, lang]);

  // SEARCH-PERF-169A — mark result paint when search view has outcomes.
  useEffect(() => {
    if (view.name !== "search") return;
    markSearchPerf("search_results_received");
    requestAnimationFrame(() => markSearchPerf("search_results_painted"));
  }, [view, searchOutcome]);

  const searchResults = searchOutcome.products;
  const searchZeroPhrase = searchOutcome.zeroPhrase;
  const searchRuHint =
    view.name === "search" &&
    searchResults.length > 0 &&
    isLikelyRussianFoodQuery(view.query)
      ? RU_SEARCH_SUCCESS_HINT_UK
      : null;

  useEffect(() => {
    if (view.name !== "search" || !serverSearchMeta || serverSearchMeta.pending) return;
    if (serverSearchMeta.query !== (view.name === "search" ? view.query : "")) return;
    // SEARCH_ERROR_SEMANTICS — do not emit zero-results on SEARCH_FAILED.
    if (serverSearchMeta.error === true) return;
    if (searchResults.length === 0) {
      emitEvent("search_zero_results", {
        original_query: serverSearchMeta.query,
        normalized_query: foldToken(serverSearchMeta.query),
        result_count: 0,
        search_version: "server_search_index_v1",
        matched_field: null,
        correction: serverSearchMeta.correction,
        did_you_mean: serverSearchMeta.didYouMean,
      });
      emitEvent("search_no_results", {
        original_query: serverSearchMeta.query,
        normalized_query: foldToken(serverSearchMeta.query),
        result_count: 0,
        search_version: "server_search_index_v1",
        search_scope: "GLOBAL_CATALOG",
      });
      return;
    }
    emitEvent("search_results_shown", {
      original_query: serverSearchMeta.query,
      normalized_query: foldToken(serverSearchMeta.query),
      result_count: searchResults.length,
      matched_field: serverSearchHits[0]?.match_reason ?? null,
      identifier_type: null,
      search_version: "server_search_index_v1",
      search_scope: "GLOBAL_CATALOG",
      catalog_revision: serverSearchMeta.catalogRevision,
      shown_product_ids: searchResults.slice(0, 20).map((p) => p.id),
    });
  }, [view, serverSearchMeta, searchResults, serverSearchHits]);

  const brands = useMemo(
    () => (brandIndex.length > 0 ? brandIndex : bundle ? buildBrandIndex(bundle.products) : []),
    [brandIndex, bundle],
  );
  const countries = useMemo(
    () => (countryIndex.length > 0 ? countryIndex : bundle ? buildCountryIndex(bundle.products) : []),
    [countryIndex, bundle],
  );

  let title = "AsiaStyle";
  if (view.name === "category") title = tileName(view.categoryId, lang);
  else if (view.name === "search") title = `${t(lang, "search")}: ${view.query}`;
  else if (view.name === "product") title = t(lang, "product");
  else if (view.name === "cart") title = t(lang, "cart");
  else if (view.name === "checkout") title = t(lang, "checkout");
  else if (view.name === "profile") title = t(lang, "profile");
  else if (view.name === "profile-edit") title = lang === "en" ? "Profile" : "Профіль";
  else if (view.name === "delivery-addresses")
    title = lang === "en" ? "My delivery addresses" : "Мої адреси доставки";
  else if (view.name === "buyer-profiles") title = lang === "en" ? "Profile" : "Профіль";
  else if (view.name === "success") title = t(lang, "success");
  else if (view.name === "new") title = "Нові надходження";
  else if (view.name === "promotions") title = lang === "en" ? "Deals" : "Акції";
  else if (view.name === "dropshipping") title = "Дропшипінг";
  else if (view.name === "bonuses") title = lang === "en" ? "Bonuses and Referral Program" : "Бонуси та реферальна програма";
  else if (view.name === "brands") title = t(lang, "brands");
  else if (view.name === "brand") title = brands.find((brand) => brand.slug === view.slug)?.name ?? t(lang, "brands");
  else if (view.name === "countries") title = t(lang, "countries");
  else if (view.name === "country") {
    const c = countries.find((row) => row.country_id === view.countryId);
    title = (lang === "en" ? c?.labelEn : c?.labelUk) ?? t(lang, "countries");
  } else if (view.name === "orders") title = lang === "en" ? "My orders" : "Мої замовлення";
  else if (view.name === "messages") title = lang === "en" ? "My messages" : "Мої повідомлення";
  else if (view.name === "order-detail") title = lang === "en" ? "Order" : "Замовлення";
  else if (view.name === "invite-friends")
    title = lang === "en" ? "Share “Скарби Азії”" : "Поділися “Скарбами Азії”";
  else if (view.name === "engagement-hub")
    title = lang === "en" ? "My adventures" : "Мої пригоди";
  else if (view.name === "ime-debug") title = "IME debug";

  /** Pages with an inline text search row — photo search sits beside that row (not only in Chrome). */
  const inlineSearchViews = new Set([
    "home",
    "new",
    "brand",
    "country",
    "category",
    "search",
    "brands",
    "product",
    "promotions",
  ]);
  /** F4 — camera is discovery-only; never crowd Cart/Checkout transactional headers. */
  const transactionalNoCameraViews = new Set([
    "cart",
    "checkout",
    "success",
    "orders",
    "order-detail",
    "documents-home",
    "document-detail",
    "messages",
  ]);
  // ASIASTYLE-R7.1.1 — production visual / photo search (VISUAL-SEARCH-001 stack).
  const SOFT_LAUNCH_HIDE_VISUAL_SEARCH = false;
  const visualSearchNode = SOFT_LAUNCH_HIDE_VISUAL_SEARCH ? null : (
    <LazyVisualSearchSlot
      key={routeKey}
      lang={lang}
      sessionKey={sessionKey}
      onOpenProduct={openProduct}
      onAddToCart={(productId, seed) => {
        const fromCatalog = productById.get(productId) ?? productShellByIdRef.current.get(productId);
        if (fromCatalog) {
          handleAddRecommendation(fromCatalog, "VISUAL_SIMILAR");
          return;
        }
        if (seed && (seed.title || seed.priceUah != null)) {
          const shell = hitToCatalogProduct({
            id: productId,
            title: String(seed.title ?? productId),
            brand: null,
            thumbnail: seed.imageUrl ?? null,
            availability: "in_stock",
            price: typeof seed.priceUah === "number" ? seed.priceUah : null,
            match_reason: "visual_search_seed",
            type: "product",
          });
          handleAddRecommendation(shell, "VISUAL_SIMILAR");
          return;
        }
        handleAddRecommendation(productId, "VISUAL_SIMILAR");
      }}
      onOpenCart={openCart}
      qtyOf={(id) => cart.find((i) => i.id === id)?.qty ?? 0}
      emit={(name, payload) => emitEvent(name as never, payload)}
      resetKey={routeKey}
    />
  );

  let content: ReactNode;

  if (view.name === "success") {
    const successMethod =
      view.paymentMethod === "bank_transfer_fop" ||
      view.paymentMethod === "nova_poshta_cod" ||
      view.paymentMethod === "novapay_card"
        ? view.paymentMethod
        : null;
    const isNovapay = successMethod === "novapay_card";
    const isCod = successMethod === "nova_poshta_cod";
    const isFop = successMethod === "bank_transfer_fop";
    const awaitingStock =
      isFop && view.paymentTiming === "after_manager_confirmation" && !bankConfirmedPaid;
    const novapayPhase = resolveNovapayUiPhase({
      paymentMethod: successMethod,
      paymentReturn,
      lifecycleStatus: novapayLifecycleStatus,
      orderId: view.orderId,
    });
    const verifying = novapayPhase === "returned_pending_confirmation";
    const awaitingPayment = novapayPhase === "awaiting_payment" || novapayPhase === "preparing_handoff";
    const showPaidBento = novapayPhase === "paid" || isCod;
    const bentoVariant = isCod ? "cod" : isFop && fopReviewActive ? "fop_review" : "online_paid";
    const allowMethodChange = !isNovapay || canChangePaymentMethod(novapayPhase);
    void allowMethodChange;
    const paymentMethodLabel = isNovapay
      ? lang === "en"
        ? "Online payment"
        : "Онлайн-оплата"
      : isCod
        ? t(lang, "success_cod_payment_row")
        : lang === "en"
          ? "FOP transfer"
          : "Рахунок ФОП";
    const openRules = () => {
      emitEvent("referral_rules_opened", { source: "success_card" });
      emitEvent("rules_opened", { rules_version: BONUS_RULES_VERSION });
      push({ name: "bonuses" });
    };
    content = (
      <CommercePageShell wide={showPaidBento}>
      <div
        className="sv2-body"
        data-testid="sv2-success-surface"
        data-payment-method={successMethod ?? "unknown"}
        data-novapay-phase={isNovapay ? novapayPhase : successMethod ?? "other"}
      >
        {showPaidBento ? (
          <PaidBentoDashboardV2
            lang={lang}
            displayOrderNumber={view.displayNumber ?? view.orderId}
            totalUah={view.totalUah ?? null}
            paymentMethodLabel={paymentMethodLabel}
            fulfillmentLabel={
              isCod
                ? lang === "en"
                  ? "Order accepted"
                  : "Замовлення прийнято"
                : t(lang, "fulfillment_manager_pending")
            }
            referral={referral}
            onOpenRules={openRules}
            onContactManager={() =>
              contactManager("success", view.orderId, {
                payment_method: view.paymentMethod ?? null,
                display_order_number: view.displayNumber ?? null,
              })
            }
            onHome={home}
            onCatalog={navigateHomeNow}
            onViewOrder={() => openOrderDetail(view.orderId)}
            onReferralShared={() => markReferralSharedForOrder(view.orderId)}
            onCloseStore={finishCloseStore}
            variant={bentoVariant}
          />
        ) : (
          <>
            <StatusHeroCard
              phase={verifying ? "verifying" : awaitingPayment ? "awaiting" : isFop ? "fop" : "other"}
              title={
                verifying
                  ? t(lang, "success_verifying_title")
                  : isFop && bankConfirmedPaid
                    ? t(lang, "bank_paid_title")
                    : awaitingStock
                      ? t(lang, "success_fop_title")
                    : isNovapay
                      ? t(lang, "success")
                      : t(lang, "success_fop_title")
              }
              {...(() => {
                if (verifying) return {};
                if (isFop && bankConfirmedPaid) return {};
                if (awaitingStock) return { status: t(lang, "bank_awaiting_stock") };
                if (isFop) return { status: t(lang, "bank_awaiting_confirmation") };
                if (isNovapay) return { status: t(lang, "success_awaiting_payment") };
                return { status: t(lang, "success_fop_status") };
              })()}
              {...(verifying
                ? { body: t(lang, "success_verifying_body") }
                : isNovapay
                  ? { body: t(lang, "success_awaiting_payment_body") }
                  : awaitingStock
                    ? { body: t(lang, "bank_awaiting_stock_body") }
                  : isFop
                    ? { body: bankConfirmedPaid ? t(lang, "bank_paid_body") : t(lang, "bank_awaiting_body") }
                    : {})}
            >
              <p style={{ marginTop: 14 }}>
                {t(lang, "order_number")}
                <br />
                <span className="sv2-order-id" data-testid="sv2-public-order-number">
                  {view.displayNumber ?? view.orderId}
                </span>
              </p>
            </StatusHeroCard>
            {awaitingStock ? (
              <div className="sv2-info-card" data-testid="sv2-awaiting-stock-no-pay" style={{ marginTop: 12 }}>
                <p className="sv2-muted" style={{ margin: 0, fontSize: 14 }}>
                  {t(lang, "bank_awaiting_stock_body")}
                </p>
              </div>
            ) : (
            <OrderPaymentPanelV2
              key={`${view.orderId}:${successMethod ?? "unknown"}`}
              lang={lang}
              sessionKey={sessionKey}
              orderId={view.orderId}
              displayOrderNumber={view.displayNumber ?? null}
              contactName={
                view.contactName ??
                (lastCheckoutSnapshot
                  ? `${lastCheckoutSnapshot.name} ${lastCheckoutSnapshot.surname}`.trim()
                  : null)
              }
              contactPhoneE164={view.contactPhone ?? lastCheckoutSnapshot?.phone ?? null}
              totalUah={view.totalUah ?? null}
              initialMethod={successMethod}
              bankPayment={bankPayload ?? view.bankPayment ?? null}
              verifyingReturn={verifying}
              autoStartNovapay={false}
              allowMethodChange={false}
              suppressRecoveryActions
              orderPaymentStatus={bankConfirmedPaid ? "payment_confirmed_bank_iban" : "payment_pending"}
              fopReceiptCtaRef={successFopReceiptCtaRef}
              onFopReviewStateChange={setFopReviewActive}
              onPaymentStatusChange={(status) => setNovapayLifecycleStatus(status)}
              onOpenLegalHub={() => openLegalDocument("payment")}
              suppressPaymentIntro
              autoAttemptBankHandoff={Boolean(view.autoBankHandoff)}
            />
            )}
            {/* CUSTOMER-COMMS-140-R1 — payment recovery lives on order detail; success uses one stack. */}
            <PaymentMethodChangeSheetV2
              lang={lang}
              open={successMethodSheetOpen && enabledCustomerCheckoutPaymentMethods().length > 1}
              currentMethod={successMethod}
              paymentStatus={novapayLifecycleStatus === "paid" ? "paid" : "payment_pending"}
              onlinePaymentEnabled={!isNovapayCheckoutDegradedStaging()}
              deliveryMethod="nova_poshta_warehouse"
              deliveryCity={lastCheckoutSnapshot?.city ?? null}
              deliveryWarehouse={lastCheckoutSnapshot?.warehouse ?? null}
              orderStatus="placed"
              busy={successMethodBusy}
              saveError={successMethodSaveError}
              onClose={() => {
                setSuccessMethodSheetOpen(false);
                setSuccessMethodSaveError(null);
              }}
              onContactManager={() => {
                void customerOrderActionV2({
                  sessionKey,
                  orderId: view.orderId,
                  action: "manager_help",
                });
                contactManager("success", view.orderId, {
                  payment_method: view.paymentMethod ?? null,
                  display_order_number: view.displayNumber ?? null,
                });
              }}
              onSave={(next: PaymentMethodChoiceId, meta) => {
                if (successMethodBusy) return;
                setSuccessMethodBusy(true);
                setSuccessMethodSaveError(null);
                const started = Date.now();
                void customerOrderActionV2({
                  sessionKey,
                  orderId: view.orderId,
                  action: "change_payment_method",
                  paymentMethod: next,
                }).then((res) => {
                  setSuccessMethodBusy(false);
                  if (!res.ok) {
                    const msg =
                      res.message ??
                      (lang === "en" ? "Could not change payment method" : "Не вдалося змінити спосіб оплати");
                    setSuccessMethodSaveError(msg);
                    emitEvent("payment_method_change_failed", {
                      order_status: "placed",
                      current_method: successMethod,
                      selected_method: next,
                      error_code: res.error ?? "unknown",
                      duration_ms: meta.durationMs ?? Date.now() - started,
                      correlation_id: meta.correlationId,
                    });
                    showToast(msg);
                    return;
                  }
                  setSuccessMethodSheetOpen(false);
                  emitEvent("payment_method_change_succeeded", {
                    order_status: "placed",
                    current_method: successMethod,
                    selected_method: next,
                    duration_ms: meta.durationMs ?? Date.now() - started,
                    correlation_id: meta.correlationId,
                  });
                  showToast(
                    res.message ??
                      (lang === "en"
                        ? "Payment method updated — same order kept"
                        : "Спосіб оплати оновлено — те саме замовлення"),
                  );
                  setStack((prev) => {
                    const top = prev[prev.length - 1];
                    if (!top || top.name !== "success") return prev;
                    return [...prev.slice(0, -1), { ...top, paymentMethod: next }];
                  });
                  setNovapayLifecycleStatus(next === "novapay_card" ? "pending" : null);
                  setFopReviewActive(false);
                });
              }}
            />
          </>
        )}
        {!showPaidBento ? (
          <div className="sv2-success-action-stack sv2-postorder-actions" data-testid="sv2-success-action-stack">
            <button
              type="button"
              className="sv2-cta sv2-action-primary"
              data-testid="sv2-bento-view-order"
              onClick={openOrders}
            >
              {lang === "en" ? "📦 View order" : "📦 Переглянути замовлення"}
            </button>
            <div className="sv2-bento-referral" data-testid="sv2-paid-referral-slot">
              <ReferralCardV2
                lang={lang}
                referral={referral}
                surface="other"
                compact
                onOpenRules={openRules}
                onShared={() => markReferralSharedForOrder(view.orderId)}
              />
            </div>
            <button
              type="button"
              className="sv2-cta sv2-primary-commerce"
              data-testid="sv2-success-to-catalog"
              onClick={navigateHomeNow}
            >
              {lang === "en" ? "🛍 To catalog" : "🛍 До каталогу"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-action-secondary"
              data-testid="sv2-bento-contact-manager"
              onClick={() =>
                contactManager("success", view.orderId, {
                  payment_method: view.paymentMethod ?? null,
                  display_order_number: view.displayNumber ?? null,
                })
              }
            >
              {lang === "en" ? "💬 Contact manager" : "💬 Зв’язатися з менеджером"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-action-tertiary"
              data-testid="sv2-close-store"
              onClick={() => {
                tryReferralExitRescue(() => {
                  clearPersistedSuccessView();
                  finishCloseStore();
                });
              }}
            >
              {lang === "en" ? "Close store" : "Закрити магазин"}
            </button>
          </div>
        ) : null}
        <BankIbanPaymentSheetV2
          lang={lang}
          open={isFop && bankSheetOpen && !bankConfirmedPaid}
          payload={bankPayload}
          onClose={() => setBankSheetOpen(false)}
        />
        <ReferralExitRescueSheetV2
          lang={lang}
          open={exitRescueOpen}
          onShare={() => {
            markReferralSharedForOrder(view.orderId);
            setExitRescueOpen(false);
            const pending = exitRescuePendingRef.current;
            exitRescuePendingRef.current = null;
            emitEvent("referral_share_clicked", { order_id: view.orderId, source: "exit_rescue" });
            const payload = buildInviteSharePayload(lang, {
              personalReferralLink: referral?.url ?? null,
              referralCode: referral?.code ?? null,
              referralToken: referral?.token ?? null,
            });
            emitEvent("referral_share_open_attempted", {
              order_id: view.orderId,
              source: "exit_rescue",
              has_referral_token: payload.hasReferralToken,
            });
            const opened = openTelegramShareSync(payload.shareUrl);
            if (!opened.ok) {
              emitEvent("referral_share_error", {
                order_id: view.orderId,
                source: "exit_rescue",
                via: opened.via,
              });
            } else {
              emitEvent("referral_share_sent", {
                order_id: view.orderId,
                source: "exit_rescue",
                via: opened.via,
              });
            }
            pending?.();
          }}
          onDismiss={() => {
            markReferralExitDismissed(view.orderId);
            setExitRescueOpen(false);
            const pending = exitRescuePendingRef.current;
            exitRescuePendingRef.current = null;
            pending?.();
          }}
        />
      </div>
      </CommercePageShell>
    );
  } else if (
    view.name === "profile" ||
    view.name === "profile-edit" ||
    view.name === "delivery-addresses" ||
    view.name === "buyer-profiles"
  ) {
    const legalCtx = peekLegalNavContext();
    const profileEditTab =
      view.name === "buyer-profiles"
        ? ("fop" as const)
        : view.name === "profile-edit"
          ? (view.tab ?? "personal")
          : "personal";
    const cabinetSurface =
      view.name === "profile-edit" || view.name === "buyer-profiles"
        ? "profile"
        : view.name === "delivery-addresses"
          ? "delivery"
          : "root";
    content = (
      <ProfileV2
        buildId={diagEnabled ? buildId : ""}
        snapshotId={diagEnabled ? (bundle?.meta.snapshotId ?? null) : null}
        generatedAt={diagEnabled ? (bundle?.meta.generatedAt ?? null) : null}
        hideTechnicalDiagnostics={!diagEnabled}
        deleteRequested={deleteRequested}
        marketingSuppressed={marketingSuppressed}
        marketingPermissionState={marketingPermissionState}
        lang={lang}
        cartCount={cartCount}
        initialLegalPanel={profileLegalPanel}
        skipHubOnDocumentBack={legalCtx?.skipHubOnDocumentBack === true}
        onLangChange={setLanguage}
        theme={theme}
        onThemeCycle={() => setTheme((prev) => cycleStoreV2Theme(prev))}
        onDeleteData={requestDeleteData}
        onMarketingOptOut={requestMarketingOptOut}
        onMarketingOptIn={() => requestMarketingOptIn("CUSTOMER_CABINET")}
        onLeaveProfile={() => {
          if (legalCtx?.skipHubOnDocumentBack) {
            returnFromLegalDocument();
            return;
          }
          clearLegalNavContext();
          back();
        }}
        onOpenCart={openCart}
        onOpenCatalog={navigateHomeNow}
        onCloseStore={finishCloseStore}
        onOpenOrders={openOrders}
        onOpenOrderDocuments={(orderId) => {
          openOrderDetail(orderId);
          window.setTimeout(() => {
            document.querySelector<HTMLButtonElement>('[data-testid="sv2-order-tab-documents"]')?.click();
            document
              .querySelector('[data-testid="sv2-documents-section"]')
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 450);
        }}
        cabinetSessionKey={sessionKey}
        onOpenMessages={openMessages}
        onOpenInvite={openInviteFriends}
        onOpenEngagementHub={() => {
          if (resolveEngagementHubContract().enabled) openEngagementHub();
        }}
        cabinetSurface={cabinetSurface}
        initialProfileTab={profileEditTab}
        onOpenProfileForm={() => push({ name: "profile-edit", tab: "personal" })}
        onOpenDeliveryAddresses={() => push({ name: "delivery-addresses" })}
        onOpenBuyerProfiles={() => push({ name: "profile-edit", tab: "fop" })}
        cabinetProfile={
          lastCheckoutSnapshot
            ? {
                name: `${lastCheckoutSnapshot.name ?? ""} ${lastCheckoutSnapshot.surname ?? ""}`.trim(),
                phone: lastCheckoutSnapshot.phone ?? "",
                city: lastCheckoutSnapshot.city ?? "",
                warehouse: lastCheckoutSnapshot.warehouse ?? "",
                locale: lastCheckoutSnapshot.locale,
              }
            : null
        }
      />
    );
  } else if (view.name === "orders") {
    content = (
      <CustomerRouteErrorBoundary
        routeName="orders"
        lang={lang}
        onGoBack={() => customerNav.goLogicalParent("orders")}
        onGoHome={navigateHomeNow}
      >
        <MyOrdersV2
          lang={lang}
          sessionKey={sessionKey}
          onOpenOrder={openOrderDetail}
          onOpenCatalog={navigateHomeNow}
          onBack={() => customerNav.goLogicalParent("orders")}
        />
      </CustomerRouteErrorBoundary>
    );
  } else if (view.name === "messages") {
    content = (
      <CustomerRouteErrorBoundary
        routeName="messages"
        lang={lang}
        onGoBack={() => customerNav.goLogicalParent("messages")}
        onGoHome={navigateHomeNow}
      >
        <MessagesV2
          lang={lang}
          sessionKey={sessionKey}
          onOpenOrder={openOrderDetail}
          onContactManager={() => contactManager("messages")}
        />
      </CustomerRouteErrorBoundary>
    );
  } else if (view.name === "order-detail") {
    content = (
      <CustomerRouteErrorBoundary
        routeName="order-detail"
        lang={lang}
        onGoBack={() => customerNav.goLogicalParent("order-detail")}
        onGoHome={navigateHomeNow}
      >
        <OrderDetailV2
          lang={lang}
          sessionKey={sessionKey}
          orderId={view.orderId}
          referral={referral}
          onReferralShared={() => markReferralSharedForOrder(view.orderId)}
          onRepeatOrder={handleRepeatOrder}
          onContactManager={() => contactManager("order_detail", view.orderId)}
          onGoHome={navigateHomeNow}
        />
      </CustomerRouteErrorBoundary>
    );
  } else if (view.name === "invite-friends") {
    content = (
      <CustomerRouteErrorBoundary
        routeName="invite-friends"
        lang={lang}
        onGoBack={() => customerNav.goLogicalParent("invite-friends")}
        onGoHome={navigateHomeNow}
      >
        <InviteFriendsV2 lang={lang} sessionKey={sessionKey} onBack={() => customerNav.goLogicalParent("invite-friends")} />
      </CustomerRouteErrorBoundary>
    );
  } else if (view.name === "engagement-hub") {
    content = <EngagementHubPlaceholderV2 lang={lang} onBack={back} />;
  } else if (view.name === "ime-debug") {
    // COMMERCE-LAUNCH-168D2 — forensic must not wait on catalog bundle.
    content = <ImeForensicDebugV2 lang={lang} onBack={home} />;
  } else if (view.name === "home" && (bundle || homeMeta)) {
    // R4A — Home paints from meta before full catalog; suggest needs searchableProducts when ready.
    content = (
      <HomeV2
        lang={lang}
        meta={(bundle?.meta ?? homeMeta)!}
        showWelcomeBack={returningCustomer}
        welcomeName={welcomeName}
        sessionKey={sessionKey}
        visualSearchSlot={visualSearchNode}
        searchableProducts={searchableProducts}
        suggestCloseToken={suggestCloseToken}
        searchBarEpoch={searchBarEpoch}
        onOpenCategory={openCategory}
        onPrefetchCategory={(categoryId) => {
          prefetchCategoryPage(categoryId, resolveCategorySortBeforeFetch(categoryId));
        }}
        onSearch={openSearch}
        onOpenProduct={openProduct}
        onOpenBrand={(name) => {
          openBrand(brandSlug(name), "search_suggest");
        }}
        onOpenCategoryLabel={(label) => {
          const tile = STORE_V2_TILES.find(
            (t) => t.name === label || t.nameEn === label || t.name.includes(label),
          );
          const cat = (bundle?.meta.categories ?? homeMeta?.categories ?? []).find(
            (c) => c.label === label || c.label.includes(label),
          );
          if (tile) openCategory(tile.id);
          else if (cat) openCategory(cat.id);
          else openSearch(label);
        }}
        onOpenProfile={openProfile}
        onOpenNew={() => {
          openNewArrivals();
        }}
        onOpenBrands={() => {
          void loadBrandIndexV2().then((rows) => {
            if (rows.length) setBrandIndex(mapBrandIndexRows(rows));
          });
          if (brandsNavLockRef.current) return;
          brandsNavLockRef.current = true;
          push({ name: "brands" });
          window.setTimeout(() => {
            brandsNavLockRef.current = false;
          }, 450);
        }}
        onPrefetchBrands={() => {
          prefetchBrandIndexV2();
        }}
        onOpenCountries={() => {
          if (countriesNavLockRef.current) return;
          countriesNavLockRef.current = true;
          void loadCountryIndexV2().then((rows) => {
            if (rows.length) setCountryIndex(mapCountryIndexRows(rows));
          });
          push({ name: "countries" });
          window.setTimeout(() => {
            countriesNavLockRef.current = false;
          }, 450);
        }}
        onPrefetchCountries={() => {
          prefetchCountryIndexV2();
        }}
        onOpenDropshipping={() => push({ name: "dropshipping" })}
        onOpenPromotions={() => push({ name: "promotions" })}
        emitEvent={(name, payload) => emitEvent(name as never, payload)}
        recoverySlot={(() => {
          const cards: ReactNode[] = [];
          if (homeMarketingOptInVisible) {
            cards.push(
              <HomeMarketingOptInCardV2
                key="mkt-optin"
                lang={lang}
                onAccept={() => {
                  requestMarketingOptIn("HOME_CONTEXTUAL_CARD");
                  const mem = readHomeMarketingOptInMemory();
                  writeHomeMarketingOptInMemory({
                    ...mem,
                    granted_at: new Date().toISOString(),
                    last_shown_session: sessionKey,
                  });
                  setHomeMarketingOptInVisible(false);
                  emitEvent("marketing_optin_home_granted", {
                    source: "HOME_CONTEXTUAL_CARD",
                  });
                }}
                onDismiss={() => {
                  const mem = readHomeMarketingOptInMemory();
                  writeHomeMarketingOptInMemory({
                    ...mem,
                    dismissed_at: new Date().toISOString(),
                    last_shown_session: sessionKey,
                  });
                  setHomeMarketingOptInVisible(false);
                  emitEvent("marketing_optin_home_dismissed", {});
                }}
              />,
            );
          }
          const card = recoveryPayload?.primary as RecoveryCardView | undefined;
          if (card?.recovery_id) {
            const secondary = Array.isArray(recoveryPayload?.secondary_orders)
              ? (recoveryPayload?.secondary_orders as Array<{ public_order_number?: unknown; amount_uah?: unknown }>)
              : [];
            cards.push(
              <RecoveryCardV2
                key="recovery"
                lang={lang}
                card={card}
                secondaryOrders={secondary}
                onPrimary={() => {
                  emitEvent("cart_recovery_resumed" as never, { type: card.recovery_type });
                  if (card.recovery_type === "ORDER_PAYMENT" || card.recovery_type === "PAYMENT_ATTEMPT") {
                    const stg = String(card.public_order_number ?? "");
                    if (stg) openOrderDetail(stg);
                    return;
                  }
                  const catalog = [...productById.values()];
                  if (catalog.length > 0) {
                    const rv = revalidateCartAgainstCatalog(cart, catalog);
                    if (rv.priceChanged || rv.unavailableTitles.length > 0) {
                      setCart(writeCart(rv.items));
                    }
                    if (rv.priceChanged) setToast("Ціна оновилась");
                    else if (rv.unavailableTitles.length > 0) setToast("Товар тимчасово відсутній");
                    if (card.recovery_type === "CHECKOUT" && rv.unavailableTitles.length === 0) {
                      openCheckout();
                      return;
                    }
                    openCart();
                    return;
                  }
                  if (card.recovery_type === "CHECKOUT") {
                    openCheckout();
                    return;
                  }
                  openCart();
                }}
                onSecondary={() => {
                  if (card.recovery_type === "ORDER_PAYMENT" || card.recovery_type === "PAYMENT_ATTEMPT") {
                    const stg = String(card.public_order_number ?? "");
                    if (stg) openOrderDetail(stg);
                    return;
                  }
                  openCart();
                }}
                onOpenOrder={(stg) => openOrderDetail(stg)}
              />,
            );
          }
          if (cards.length === 0) return null;
          return <>{cards}</>;
        })()}
        onsiteRecSlot={
          <HomeOnsiteRecommendationsV2
            lang={lang}
            catalogById={productById}
            onAdd={(p) => handleAddRecommendation(p, "HOME_FOR_YOU")}
            onOpen={openRecommendationProduct}
            onRegisterActionable={registerActionableBatch}
            emitEvent={(name, payload) => emitEvent(name as never, payload)}
          />
        }
      />
    );
  } else if (view.name === "promotions") {
    content = (
      <Suspense fallback={<LoadingBlock label={lang === "en" ? "Loading…" : "Завантаження…"} />}>
        <PromotionsV2
          lang={lang}
          sessionKey={sessionKey}
          onOpenProduct={openProduct}
          visualSearchSlot={visualSearchNode}
        />
      </Suspense>
    );
  } else if (view.name === "search") {
    const searchError = serverSearchMeta?.error === true && serverSearchMeta.query === view.query;
    const showSlow = searchOutcome.pending && searchSlowPending;
    content = (
      <>
        {showSlow ? (
          <p
            className="sv2-body sv2-muted"
            data-testid="sv2-search-slow-pending"
            style={{ paddingBottom: 0, margin: "8px 16px 0", fontSize: 13 }}
          >
            {lang === "en" ? "Searching…" : "Шукаємо…"}
          </p>
        ) : null}
        {/* CUSTOMER-R4.15 — SEARCH_FAILED exclusive EmptyState; never nothing_found on network fail. */}
        {searchError ? (
          <div className="sv2-body sv2-route-top" data-route-top="search" data-testid="sv2-search-error">
            <EmptyState
              emoji="⚠️"
              title={
                lang === "en"
                  ? "Search failed. Please try again."
                  : "Не вдалося виконати пошук. Спробуйте ще раз."
              }
              hint={undefined}
            />
            <button
              type="button"
              className="sv2-cta"
              style={{ margin: "12px 16px" }}
              onClick={() => {
                emitEvent("search_retry_clicked", {
                  original_query: view.query,
                  search_scope: "GLOBAL_CATALOG",
                  search_origin: "search",
                });
                openSearch(view.query);
              }}
            >
              {lang === "en" ? "Retry" : "Повторити"}
            </button>
          </div>
        ) : (
          <>
        {(() => {
          const STRONG = new Set([
            "exact_barcode_or_sku",
            "exact_title",
            "exact_title_word",
            "exact_token",
            "exact_alias",
            "exact_brand",
            "brand_prefix",
            "exact_category",
            "exact_concept",
            "culinary_synonym",
            "semantic_alias",
            "morphology",
          ]);
          const kind = searchOutcome.correctionKind;
          const topReason = searchOutcome.topHit?.matchReason ?? "";
          const showBanner =
            Boolean(searchOutcome.correction) &&
            serverSearchMeta?.query === view.query &&
            serverSearchMeta?.requestId === searchOutcome.requestId &&
            (kind === "typo_correction" || kind === "keyboard_layout" || kind === "mixed_script") &&
            (!STRONG.has(topReason) || kind === "typo_correction");
          if (!showBanner) return null;
          if (kind === "typo_correction" && searchOutcome.products.length > 0) {
            return (
              <p
                className="sv2-body sv2-muted"
                style={{ paddingBottom: 0, margin: "8px 16px 0", fontSize: 13 }}
                data-testid="sv2-search-correction-banner"
              >
                {lang === "en"
                  ? `Results for “${searchOutcome.correction}”. You searched “${view.query}”.`
                  : `Результати для «${searchOutcome.correction}». Шукали «${view.query}».`}
              </p>
            );
          }
          return (
            <div className="sv2-body" style={{ paddingBottom: 0 }} data-testid="sv2-search-correction-banner">
              <button
                type="button"
                className="sv2-cta sv2-soft"
                onClick={() => openSearch(searchOutcome.correction!)}
              >
                {lang === "en"
                  ? `Did you mean “${searchOutcome.correction}”?`
                  : `Можливо, ви шукали «${searchOutcome.correction}»?`}
              </button>
            </div>
          );
        })()}
        {searchRuHint ? (
          <p
            className="sv2-body sv2-muted"
            data-testid="sv2-search-ru-hint"
            style={{ paddingBottom: 0, margin: "8px 16px 0", fontSize: 13 }}
          >
            {searchRuHint}
          </p>
        ) : null}
        <CatalogListV2
          key={collectionKeyFrom("search", view.query)}
          lang={lang}
          heading={
            (() => {
              const shown =
                searchOutcome.correction &&
                searchOutcome.correctionKind === "typo_correction" &&
                searchOutcome.products.length
                  ? searchOutcome.correction
                  : view.query;
              const count = !searchOutcome.pending && searchOutcome.products.length
                ? ` · ${searchOutcome.products.length}`
                : "";
              return lang === "en" ? `Results for “${shown}”${count}` : `Результати для «${shown}»${count}`;
            })()
          }
          currencyNote={lang === "en" ? "Prices in hryvnia" : "Ціни в гривнях"}
          products={searchResults}
          titleOf={titleOf}
          emptyTitle={searchOutcome.pending ? (lang === "en" ? "Searching…" : "Шукаємо…") : t(lang, "nothing_found")}
          emptyHint={searchOutcome.pending ? undefined : searchZeroPhrase ?? t(lang, "nothing_hint")}
          onOpenProduct={openProduct}
          onPrefetchProduct={prefetchProduct}
          onAdd={handleAdd}
          onOpenCart={openCart}
          cartItemCount={cartCount}
          sourceSurface="search"
          sourceRoute={routeKey}
          collectionKey={collectionKeyFrom("search", view.query)}
          onSearch={openSearch}
          visualSearchSlot={visualSearchNode}
          searchableProducts={searchableProducts}
          suggestCloseToken={suggestCloseToken}
          searchBarEpoch={searchBarEpoch}
          onOpenProductFromSuggest={openProduct}
          onOpenBrandFromSuggest={(name) => {
            openBrand(brandSlug(name), "search_suggest");
          }}
          qtyOf={(id) => cart.find((i) => i.id === id)?.qty ?? 0}
          onInc={handleInc}
          onDec={handleDec}
          onAvailabilityRequest={(product) => {
            openAvailabilitySheet(product);
          }}
        />
        {!searchOutcome.pending && !searchError ? (
          <SearchDiscoveryContinuationV2
            lang={lang}
            query={view.query}
            hasExactResults={searchResults.length > 0}
            anchorProductId={searchResults[0]?.id ?? null}
            catalogById={productById}
            onAdd={(p) => handleAddRecommendation(p, "SEARCH_SIMILAR")}
            onOpen={openRecommendationProduct}
            onRegisterActionable={registerActionableBatch}
            emitEvent={(name, payload) => emitEvent(name as never, payload)}
          />
        ) : null}
          </>
        )}
      </>
    );
  } else if (
    !bundle &&
    view.name !== "product" &&
    view.name !== "cart" &&
    view.name !== "checkout" &&
    view.name !== "category" &&
    view.name !== "new" &&
    view.name !== "brands" &&
    view.name !== "brand" &&
    view.name !== "countries" &&
    view.name !== "country" &&
    view.name !== "dropshipping"
  ) {
    // STAGING-LAUNCH-READINESS-002-R2-R2 — this is the ONLY branch that still
    // waits on the catalog bundle (shopping surfaces below all read `bundle`
    // directly). catalogTimedOut backstops fetchJsonMulti's own per-URL abort
    // timeout — e.g. if identity-overlay enrichment itself stalls.
    // BANK-IBAN-R3.10 — cart/checkout must render from seeded cart lines; do not
    // wait on catalog-v2.json after a search→PDP seed navigation.
    // CUSTOMER-R4.15-C — category uses incremental shards + own skeleton (no dragon).
    // CUSTOMER-R4.15-K15 — Dropshipping is informational lead-gen; never wait on catalog-v2.json.
    content = loadError || catalogTimedOut ? (
      <div className="sv2-body">
        <RouteDegradedState
          lang={lang}
          variant="store"
          onRetry={retryCatalogLoad}
          onHome={navigateHomeNow}
          onContactManager={() => contactManager("catalog_load")}
        />
      </div>
    ) : (
      <BrandedStoreLoading lang={lang} />
    );
  } else if (view.name === "new") {
    const na = newArrivalsBrowse;
    content = (
      <CatalogListV2
        key={collectionKeyFrom("new")}
        lang={lang}
        heading={t(lang, "new_arrivals")}
        currencyNote={lang === "en" ? "Prices in hryvnia" : "Ціни в гривнях"}
        products={na?.products ?? []}
        titleOf={titleOf}
        emptyTitle={lang === "en" ? "No new products yet" : "Новинок поки немає"}
        emptyHint={
          na?.loading
            ? lang === "en"
              ? "Loading fresh products…"
              : "Завантажуємо свіжі товари…"
            : na?.error
              ? lang === "en"
                ? "Could not load new arrivals."
                : "Не вдалося завантажити новинки."
              : undefined
        }
        onOpenProduct={openProduct}
        onPrefetchProduct={prefetchProduct}
        onAdd={handleAdd}
        onOpenCart={openCart}
        cartItemCount={cartCount}
        sourceSurface="new"
        sourceRoute={routeKey}
        collectionKey={collectionKeyFrom("new")}
        onSearch={openSearch}
        visualSearchSlot={visualSearchNode}
        searchableProducts={searchableProducts}
        totalCount={na?.total ?? 0}
        hasMoreRemote={Boolean(na?.hasMore) && !na?.localComplete}
        loadingMore={Boolean(na?.loadingMore)}
        onLoadMoreRemote={loadMoreNewArrivals}
        remoteSort={na?.sort ?? "newest"}
        onRemoteSortChange={changeNewArrivalsSort}
        initialLoading={!na || (na.loading && na.products.length === 0)}
        loadError={na?.error ?? null}
        onRetryLoad={() => reloadNewArrivals(na?.sort ?? "newest")}
        qtyOf={(id) => cart.find((i) => i.id === id)?.qty ?? 0}
        onInc={handleInc}
        onDec={handleDec}
      />
    );
  } else if (view.name === "brands") {
    content = (
      <BrandBrowserV2
        lang={lang}
        brands={brands}
        visualSearchSlot={visualSearchNode}
        onOpenBrand={(slug) => {
          openBrand(slug, "brand_directory");
        }}
      />
    );
  } else if (view.name === "brand") {
    const brand = brands.find((item) => item.slug === view.slug);
    const bb = brandBrowse?.slug === view.slug ? brandBrowse : null;
    content = (
      <CatalogListV2
        key={collectionKeyFrom("brand", brand?.name ?? view.slug)}
        lang={lang}
        heading={brand?.name ?? t(lang, "brands")}
        currencyNote={lang === "en" ? "Prices in hryvnia" : "Ціни в гривнях"}
        products={bb?.products ?? []}
        titleOf={titleOf}
        emptyTitle={t(lang, "nothing_found")}
        emptyHint={
          bb?.loading
            ? lang === "en"
              ? "Loading brand products…"
              : "Завантажуємо товари бренду…"
            : bb?.error
              ? lang === "en"
                ? "Could not load this brand."
                : "Не вдалося завантажити бренд."
              : undefined
        }
        onOpenProduct={openProduct}
        onPrefetchProduct={prefetchProduct}
        onAdd={handleAdd}
        onOpenCart={openCart}
        cartItemCount={cartCount}
        sourceSurface="brand"
        sourceRoute={routeKey}
        categoryId={view.slug}
        collectionKey={collectionKeyFrom("brand", brand?.name ?? view.slug)}
        onSearch={openSearch}
        visualSearchSlot={visualSearchNode}
        searchableProducts={searchableProducts}
        totalCount={bb?.total ?? 0}
        hasMoreRemote={Boolean(bb?.hasMore) && !bb?.localComplete}
        loadingMore={Boolean(bb?.loadingMore)}
        onLoadMoreRemote={loadMoreBrand}
        remoteSort={bb?.sort ?? "recommended"}
        onRemoteSortChange={changeBrandSort}
        initialLoading={!bb || (bb.loading && bb.products.length === 0)}
        loadError={bb?.error ?? null}
        onRetryLoad={() => openBrand(view.slug)}
        qtyOf={(id) => cart.find((item) => item.id === id)?.qty ?? 0}
        onInc={handleInc}
        onDec={handleDec}
      />
    );
  } else if (view.name === "countries") {
    content = (
      <CountryBrowserV2
        lang={lang}
        countries={countries}
        onOpenCountry={(countryId) => {
          openCountry(countryId, "country_directory");
        }}
      />
    );
  } else if (view.name === "country") {
    const country = countries.find((row) => row.country_id === view.countryId);
    const cb = countryBrowse?.countryId === view.countryId ? countryBrowse : null;
    content = (
      <CatalogListV2
        key={collectionKeyFrom("country", view.countryId)}
        lang={lang}
        heading={
          country
            ? `${country.flagEmoji} ${lang === "en" ? country.labelEn : country.labelUk}`
            : t(lang, "countries")
        }
        currencyNote={lang === "en" ? "Prices in hryvnia" : "Ціни в гривнях"}
        products={cb?.products ?? []}
        titleOf={titleOf}
        emptyTitle={t(lang, "nothing_found")}
        emptyHint={
          cb?.loading
            ? lang === "en"
              ? "Loading country products…"
              : "Завантажуємо товари країни…"
            : t(lang, "country_filter_meaning")
        }
        onOpenProduct={openProduct}
        onPrefetchProduct={prefetchProduct}
        onAdd={handleAdd}
        onOpenCart={openCart}
        cartItemCount={cartCount}
        sourceSurface="country"
        sourceRoute={routeKey}
        categoryId={view.countryId}
        collectionKey={collectionKeyFrom("country", view.countryId)}
        onSearch={openSearch}
        visualSearchSlot={visualSearchNode}
        searchableProducts={searchableProducts}
        totalCount={cb?.total ?? 0}
        hasMoreRemote={Boolean(cb?.hasMore) && !cb?.localComplete}
        loadingMore={Boolean(cb?.loadingMore)}
        onLoadMoreRemote={loadMoreCountry}
        remoteSort={cb?.sort ?? "recommended"}
        onRemoteSortChange={changeCountrySort}
        initialLoading={!cb || (cb.loading && cb.products.length === 0)}
        loadError={cb?.error ?? null}
        onRetryLoad={() => openCountry(view.countryId)}
        qtyOf={(id) => cart.find((item) => item.id === id)?.qty ?? 0}
        onInc={handleInc}
        onDec={handleDec}
      />
    );
  } else if (view.name === "dropshipping") {
    content = (
      <div className="sv2-body sv2-route-top" data-route-top="dropshipping" data-testid="sv2-dropshipping-page">
        <div className="sv2-section-title">{lang === "en" ? 'Dropshipping with "Treasures of Asia"' : "Дропшипінг зі «Скарбами Азії»"}</div>
        <p>{lang === "en" ? "Sell popular Asian products without your own warehouse." : "Продавайте популярні азійські товари без власного складу."}</p>
        <ul className="sv2-benefits">
          <li>{lang === "en" ? "You find the customer" : "Ви знаходите клієнта"}</li>
          <li>{lang === "en" ? "We pack and ship the order" : "Ми комплектуємо та відправляємо замовлення"}</li>
          <li>{lang === "en" ? "You receive 3% of confirmed sales turnover" : "Ви отримуєте 3% від обороту підтверджених продажів"}</li>
          <li>{lang === "en" ? "Current catalogue and delivery across Ukraine" : "Актуальний каталог і відправлення по Україні"}</li>
          <li>{lang === "en" ? "A manager helps you get started" : "Менеджер допоможе з початком роботи"}</li>
        </ul>
        <div className="sv2-info-card sv2-dropshipping-steps">
          <strong>{lang === "en" ? "How it works" : "Як це працює"}</strong>
          <ol>
            <li>{lang === "en" ? "Choose products from the current catalogue." : "Оберіть товари з актуального каталогу."}</li>
            <li>{lang === "en" ? "Send the confirmed customer order to the manager." : "Передайте менеджеру підтверджене замовлення клієнта."}</li>
            <li>{lang === "en" ? "We ship available stock and record confirmed turnover." : "Ми відправимо наявний товар і зафіксуємо підтверджений оборот."}</li>
          </ol>
        </div>
        <button
          type="button"
          className="sv2-cta"
          data-testid="sv2-dropshipping-contact"
          onClick={() => {
            emitEvent("dropshipping_lead_requested", { source: "dropshipping_page" });
            contactManager("dropshipping");
          }}
        >
          {t(lang, "contact_manager")}
        </button>
      </div>
    );
  } else if (view.name === "bonuses") {
    content = (
      <div className="sv2-body sv2-route-top sv2-reading-shell" data-route-top="bonuses" data-testid="sv2-referral-rules">
        <div className="sv2-section-title">
          {lang === "en" ? "Bonuses and referral program" : "Бонуси та реферальна програма"}
        </div>
        <p className="sv2-muted">
          {lang === "en" ? "Rules version" : "Версія правил"}: {BONUS_RULES_VERSION} · {BONUS_RULES_EFFECTIVE_DATE}
        </p>
        <div className="sv2-info-card sv2-rules-summary">
          <p>
            {lang === "en"
              ? "A personal marketing discount — not cash and not a payout."
              : "Персональна маркетингова знижка — не гроші і не виплата."}
          </p>
        </div>
        {(lang === "en"
          ? [
              ["What your friend gets", "3% on the first paid and successfully fulfilled order."],
              ["What you get", "3% on your next purchase after their order is fulfilled."],
              ["Minimum merchandise total", "700 UAH (delivery excluded)."],
              ["Maximum discount", "150 UAH per order; combined discounts max 10%."],
              ["Validity", "30 days after activation."],
              ["Delivery", "Carrier fees are not counted toward the minimum."],
              ["Compatibility", "Does not stack with other promos unless an offer says so; the best single option applies."],
              ["Cancellation and returns", "Cancelled, unreceived, returned or fraudulent orders do not activate the bonus."],
              ["Abuse protection", "Self-referral and duplicate accounts are blocked; anti-fraud hold applies."],
            ]
          : [
              ["Що отримає друг", "3% на перше оплачене й успішно виконане замовлення."],
              ["Що отримаєте ви", "3% на наступну покупку після виконання замовлення друга."],
              ["Мінімальна сума товарів", "700 ₴ (без доставки)."],
              ["Максимальна знижка", "150 ₴ на замовлення; сумарно з іншими — до 10%."],
              ["Термін дії", "30 днів після активації."],
              ["Доставка", "Вартість доставки не входить у мінімальну суму."],
              ["Сумісність з іншими акціями", "Не сумується з іншими акціями, якщо інше прямо не зазначено; застосовується найвигідніший варіант."],
              ["Скасування та повернення", "Скасовані, не отримані, повернуті або шахрайські замовлення бонус не активують."],
              ["Захист від зловживань", "Self-referral і дублікати акаунтів блокуються; діє anti-fraud hold."],
            ]
        ).map(([h, p]) => (
          <section key={h} className="sv2-rules-section">
            <h3>{h}</h3>
            <p>{p}</p>
          </section>
        ))}
      </div>
    );
  } else if (view.name === "category") {
    const browse =
      categoryBrowse?.categoryId === view.categoryId ? categoryBrowse : null;
    const products = browse?.products ?? [];
    content = (
      <CatalogListV2
        key={`${collectionKeyFrom("category", view.categoryId)}:${browse?.sort ?? "pending"}`}
        lang={lang}
        heading={tileLabel(view.categoryId, lang)}
        currencyNote={lang === "en" ? "Prices in hryvnia" : "Ціни в гривнях"}
        products={products}
        titleOf={titleOf}
        emptyTitle={t(lang, "empty_category")}
        emptyHint={t(lang, "empty_category_hint")}
        onOpenProduct={openProduct}
        onPrefetchProduct={prefetchProduct}
        onAdd={handleAdd}
        onOpenCart={openCart}
        cartItemCount={cartCount}
        sourceSurface="category"
        sourceRoute={routeKey}
        categoryId={view.categoryId}
        collectionKey={collectionKeyFrom("category", view.categoryId)}
        onSearch={openSearch}
        visualSearchSlot={visualSearchNode}
        searchableProducts={searchableProducts}
        qtyOf={(id) => cart.find((i) => i.id === id)?.qty ?? 0}
        onInc={handleInc}
        onDec={handleDec}
        onAvailabilityRequest={(product) => {
          openAvailabilitySheet(product);
        }}
        totalCount={browse?.total || products.length}
        hasMoreRemote={Boolean(browse?.hasMore)}
        loadingMore={Boolean(browse?.loadingMore)}
        onLoadMoreRemote={loadMoreCategory}
        remoteSort={browse?.sort ?? "recommended"}
        onRemoteSortChange={changeCategorySort}
        initialLoading={!browse || browse.loading}
        loadError={browse?.error ?? null}
        onRetryLoad={() => openCategory(view.categoryId, browse?.sort)}
      />
    );
  } else if (view.name === "product") {
    markPdpPerf("pdp_route_ts");
    const seed = productShellByIdRef.current.get(view.productId) ?? null;
    const hydrated = productById.get(view.productId) ?? null;
    const product = mergePdpHydrationKeepingCommercialPrice(seed, hydrated);
    if (product && !productShellByIdRef.current.has(product.id)) {
      productShellByIdRef.current.set(product.id, product);
    }
    if (product) markPdpPerf("pdp_seed_render_ts");
    const pdpRecs = product && bundle ? recommendForProduct(bundle.products, product, lang, 3) : [];
    const seedHasCommercial =
      Boolean(product?.title) && typeof product?.priceUah === "number" && product.priceUah >= 1;
    if (seedHasCommercial) markPdpPerf("pdp_detail_ready_ts");
    content = product ? (
      <ProductV2
        lang={lang}
        product={product}
        displayTitle={titleOf(product)}
        recommendations={pdpRecs}
        inCartQty={cartSkuQuantity(cart, product.id)}
        cartTotalQty={cartCount}
        qtyOf={(id) => cartSkuQuantity(cart, id)}
        onAdd={handleAdd}
        onGoCart={openCart}
        onAddRecommendation={(p, surface) => handleAddRecommendation(p, surface ?? "PDP_CROSS_SELL")}
        onOpenRecommendation={openRecommendationProduct}
        onRegisterActionable={registerActionableBatch}
        onInc={() => handleInc(product.id)}
        onDec={() => handleDec(product.id)}
        onSearch={openSearch}
        visualSearchSlot={visualSearchNode}
        shellHydrating={!seedHasCommercial}
        onAvailabilityRequest={() => {
          openAvailabilitySheet(product);
        }}
        availabilityMessage={
          availabilityFeedback?.productId === product.id ? availabilityFeedback.message : null
        }
        onContactManager={() =>
          contactManager("availability_followup", undefined, {
            product_id: product.id,
            sku: product.sku,
            title: titleOf(product),
            brand: product.brand,
            category: product.categoryName,
          })
        }
      />
    ) : (
      <EmptyState
        emoji="🤔"
        title={lang === "en" ? "Product not found" : "Товар не знайдено"}
      />
    );
    if (!product) {
      emitEvent("customer_route_unresolved", {
        source_surface: "product_pdp",
        product_id: view.productId,
        catalog_revision: (bundle as { meta?: { catalog_revision?: string } } | null)?.meta?.catalog_revision ?? null,
      });
    }
  } else if (view.name === "cart") {
    content = (
      <CartV2View
        lang={lang}
        items={cart}
        promoCode={promoCode}
        recommendations={cartRecommendations}
        onInc={handleInc}
        onDec={handleDec}
        onRemove={(id) => setCart(removeItem(id))}
        onApplyPromo={applyPromo}
        onRemovePromo={removePromo}
        onCheckout={openCheckout}
        onContinue={home}
        onOpenProduct={openProduct}
        onAddRecommendation={(p) => handleAddRecommendation(p, "CART_COMPLEMENT")}
        onRegisterActionable={registerActionableBatch}
        onRecommendationsView={(ids) => {
          emitEvent("crosssell_view", { product_ids: ids, count: ids.length });
          emitEvent("cart_recommendation_impression", { product_ids: ids, count: ids.length });
          emitEvent("recommendation_impression", {
            placement: "CART_CROSS_SELL",
            rotation_index: journeyRecRotationIndex,
            product_ids: ids,
            cart_product_ids: cart.map((i) => i.id),
          });
        }}
        onRecommendationsRefresh={() => {
          refreshJourneyRecommendations("CART_CROSS_SELL");
        }}
        canRefreshRecommendations={journeyRecCanRefresh}
        recommendationsRefreshing={journeyRecRefreshing}
        recommendationsLoading={journeyRecLoading}
        reorderBanner={(() => {
          const session = peekReorderSession();
          if (!session || session.abandoned || session.newPublicOrderNumber) return null;
          return lang === "en"
            ? session.itemCountAdded === 0
              ? t(lang, "reorder_item_unavailable")
              : session.cartHadItemsBefore
                ? t(lang, "reorder_cart_merged")
                : t(lang, "reorder_cart_added", { STG: session.sourcePublicOrderNumber })
            : reorderCartBannerUk(session);
        })()}
        reorderUnavailable={peekReorderSession()?.unavailable ?? []}
        reorderCommentHint={
          peekReorderSession() && !peekReorderSession()?.newPublicOrderNumber
            ? t(lang, "reorder_comment_hint")
            : null
        }
        onAddSearchHit={(hit) => {
          const existing = productById.get(hit.id);
          if (existing) {
            handleAdd(existing);
            return;
          }
          if (hit.priceUah == null || !(hit.priceUah > 0)) return;
          setCart(
            addToCart({
              id: hit.id,
              title: hit.title,
              priceUah: hit.priceUah,
              imageUrl: hit.imageUrl,
              sku: null,
              brand: null,
              weight: null,
              maxQty: null,
              stockStatus: hit.availability ?? "unknown",
              availabilityLabel: lang === "en" ? "In stock" : "В наявності",
            }),
          );
          emitEvent("add_to_cart", { product_id: hit.id, price: hit.priceUah, promotion_active: false });
        }}
        onClearCartRequest={() => emitEvent("cart_clear_requested", { item_count: cart.length })}
        onClearCartCancel={() => emitEvent("cart_clear_cancelled", { item_count: cart.length })}
        onClearCart={() => {
          const next = clearCart();
          setCart(next);
          setPromoCodeState(null);
          persistPromoCode(null);
          emitEvent("cart_cleared", { item_count: 0 });
        }}
      />
    );
  } else if (view.name === "checkout") {
    content = (
      <CheckoutV2View
        lang={lang}
        sessionKey={sessionKey}
        items={cart}
        promoCode={promoCode}
        onInc={handleInc}
        onDec={handleDec}
        onRemove={(id) => {
          const next = removeItem(id);
          setCart(next);
          if (next.length === 0) openCart();
        }}
        onOpenBuyerProfiles={() => push({ name: "profile-edit", tab: "fop" })}
        onAddSearchHit={(hit) => {
          const existing = productById.get(hit.id);
          if (existing) {
            handleAdd(existing);
            return;
          }
          if (hit.priceUah == null || !(hit.priceUah > 0)) return;
          setCart(
            addToCart({
              id: hit.id,
              title: hit.title,
              priceUah: hit.priceUah,
              imageUrl: hit.imageUrl,
              sku: null,
              brand: null,
              weight: null,
              maxQty: null,
              stockStatus: hit.availability ?? "unknown",
              availabilityLabel: lang === "en" ? "In stock" : "В наявності",
            }),
          );
          emitEvent("add_to_cart", { product_id: hit.id, price: hit.priceUah, promotion_active: false });
        }}
        recommendations={cartRecommendations.slice(0, 3)}
        onAddRecommendation={handleAddCheckoutRecommendation}
        onOpenRecommendation={openRecommendationProduct}
        onRegisterActionable={registerActionableBatch}
        onRecommendationImpression={(ids) => {
          emitEvent("checkout_recommendation_impression", { product_ids: ids, source: "checkout" });
          emitEvent("recommendation_impression", {
            placement: "CHECKOUT_CROSS_SELL",
            rotation_index: journeyRecRotationIndex,
            product_ids: ids,
            cart_product_ids: cart.map((i) => i.id),
          });
        }}
        onRecommendationsRefresh={() => {
          refreshJourneyRecommendations("CHECKOUT_CROSS_SELL");
        }}
        canRefreshRecommendations={journeyRecCanRefresh}
        recommendationsRefreshing={journeyRecRefreshing}
        recommendationsLoading={journeyRecLoading}
        onOpenLegalOffer={() => openLegalFromSource("offer", "checkout")}
        onOpenLegalPrivacy={() => openLegalFromSource("privacy", "checkout")}
        onTrustedPrice={(productId, unitPriceUah) => {
          setCart(updateItemPrice(productId, unitPriceUah));
        }}
        onSuccess={(orderId, status, displayNumber, paymentMethod, meta) => {
          enterPostOrderSuccess(orderId, status, displayNumber, paymentMethod, meta);
        }}
      />
    );
  } else {
    content = <EmptyState emoji="🍜" title="AsiaStyle" />;
  }

  const canGoBack = stack.length > 1 && view.name !== "success";

  // R7 — finish server consent/payret bootstrap before consent gate (prevents nested consent WebView).
  if (
    !directBrowserOk &&
    !hasActivePaymentResume() &&
    !readPayretStartParam() &&
    !bootstrapping
  ) {
    return (
      <DirectBrowserEntry
        lang={lang}
        theme={theme}
        onLangChange={setLanguage}
        onContinueInBrowser={() => setDirectBrowserOk(true)}
        onBrowserCloseFallback={onBrowserCloseFallback}
      />
    );
  }

  if (runtimeRevBlocked) {
    return (
      <div className="sv2-root" data-testid="sv2-stale-runtime" data-store-scroll-root="true">
        <div className="sv2-header sv2-header--minimal" />
        <p style={{ padding: 24, fontSize: 16 }}>Застаріла версія магазину. Оновлюємо…</p>
        <OwnerRuntimeStripV2
          buildId={buildId}
          appRev={String(import.meta.env.VITE_APP_REV ?? "")}
          bootstrap={customerBootstrap ?? getCachedCustomerBootstrap()}
          consentAccepted={false}
        />
      </div>
    );
  }

  if (bootstrapping) {
    const resumeLabel =
      hasActivePaymentResume() || readPaymentResume()
        ? lang === "en"
          ? "Checking payment…"
          : "Перевіряємо оплату…"
        : lang === "en"
          ? "Restoring order…"
          : "Відновлюємо замовлення…";
    return (
      <div className="sv2-root" data-store-scroll-root="true" data-testid="sv2-payret-bootstrap">
        <div className="sv2-header sv2-header--minimal">
          <div className="sv2-header-leading">
            <StoreV2GlobalClose
              lang={lang}
              routeClass="payret_bootstrap"
              onBrowserCloseFallback={onBrowserCloseFallback}
              onRequestExitSheet={requestExitSheet}
            />
          </div>
        </div>
        <LoadingBlock label={resumeLabel} />
        <ExitRetentionSheetV2
          open={exitSheetOpen}
          lang={lang}
          state={exitRetentionState}
          cartCount={cartCount}
          referralAvailable={Boolean(referral)}
          recentProducts={exitSheetRecentProducts}
          onContinueShopping={() => setExitSheetOpen(false)}
          onOpenCart={() => setExitSheetOpen(false)}
          onOpenInvite={() => setExitSheetOpen(false)}
          onOpenProduct={() => setExitSheetOpen(false)}
          onConfirmClose={() => {
            setExitSheetOpen(false);
            emitEvent("surface_close_clicked", {
              surface: "customer",
              route_class: "payret_bootstrap",
              telegram_context: isTelegramMiniAppContext(),
              dirty: exitRetentionState === "cart" || exitRetentionState === "checkout",
              result: "closed_via_exit_sheet",
              build_id: STORE_V2_BUILD_ID,
            });
            finishCloseStore();
          }}
          onDismiss={() => setExitSheetOpen(false)}
        />
      </div>
    );
  }

  // 103: mandatory consent — catalog is not mounted until the customer accepts.
  if (!consentAccepted) {
    return (
      <div className="sv2-root sv2-root--consent" data-store-scroll-root="true">
        {consentView === "details" ? (
          <ConsentDetails
            lang={lang}
            onLangChange={setLanguage}
            theme={theme}
            onThemeCycle={() => setTheme((prev) => cycleStoreV2Theme(prev))}
            onAccept={acceptConsent}
            onBack={() => setConsentView("gate")}
            onBrowserCloseFallback={onBrowserCloseFallback}
            busy={consentBusy}
            error={consentError}
          />
        ) : consentView === "legal" && consentLegalPanel ? (
          <>
            <div className="sv2-header sv2-header--minimal">
              <div className="sv2-header-leading">
                <StoreV2GlobalClose
                  lang={lang}
                  routeClass="consent_legal"
                  onBrowserCloseFallback={onBrowserCloseFallback}
                  onRequestExitSheet={requestExitSheet}
                />
              </div>
            </div>
            <ExitRetentionSheetV2
              open={exitSheetOpen}
              lang={lang}
              state={exitRetentionState}
              cartCount={cartCount}
              referralAvailable={Boolean(referral)}
              recentProducts={exitSheetRecentProducts}
              onContinueShopping={() => setExitSheetOpen(false)}
              onOpenCart={() => setExitSheetOpen(false)}
              onOpenInvite={() => setExitSheetOpen(false)}
              onOpenProduct={() => setExitSheetOpen(false)}
              onConfirmClose={() => {
                setExitSheetOpen(false);
                emitEvent("surface_close_clicked", {
                  surface: "customer",
                  route_class: "consent_legal",
                  telegram_context: isTelegramMiniAppContext(),
                  dirty: exitRetentionState === "cart" || exitRetentionState === "checkout",
                  result: "closed_via_exit_sheet",
                  build_id: STORE_V2_BUILD_ID,
                });
                finishCloseStore();
              }}
              onDismiss={() => setExitSheetOpen(false)}
            />
            <ProfileV2
              buildId=""
              lang={lang}
              hideTechnicalDiagnostics
              initialLegalPanel={consentLegalPanel}
              standaloneDocument
              onLangChange={setLanguage}
              theme={theme}
              onThemeCycle={() => setTheme((prev) => cycleStoreV2Theme(prev))}
              onLeaveProfile={() => {
                setConsentLegalPanel(null);
                setConsentView("gate");
              }}
              onCloseStore={finishCloseStore}
            />
          </>
        ) : (
          <ConsentGate
            lang={lang}
            onLangChange={setLanguage}
            theme={theme}
            onThemeCycle={() => setTheme((prev) => cycleStoreV2Theme(prev))}
            onAccept={acceptConsent}
            onOpenOffer={() => {
              setConsentLegalPanel("offer");
              setConsentView("legal");
            }}
            onOpenPrivacy={() => {
              setConsentLegalPanel("privacy");
              setConsentView("legal");
            }}
            onBrowserCloseFallback={onBrowserCloseFallback}
            busy={consentBusy}
            error={consentError}
          />
        )}
        {consentView === "gate" || consentView === "legal" ? (
          <QuickScrollControls lang={lang} hide={false} />
        ) : null}
        <OwnerRuntimeStripV2
          buildId={buildId}
          appRev={String(import.meta.env.VITE_APP_REV ?? "")}
          bootstrap={customerBootstrap ?? getCachedCustomerBootstrap()}
          consentAccepted={consentAccepted}
        />
        {diagEnabled ? (
          <>
            <div className="sv2-badge">
              <button type="button" className="sv2-diag-open" onClick={() => setShowDiag(true)}>
                🩺
              </button>
            </div>
            {showDiag ? (
              <DiagnosticsV2
                buildId={buildId}
                consentAccepted={consentAccepted}
                onResetCache={resetStoreCache}
                onClose={() => setShowDiag(false)}
              />
            ) : null}
          </>
        ) : null}
      </div>
    );
  }

  // COMMERCE-LAUNCH-167A-R4 — KEEP ↑↓ QuickScrollControls; no side product elevator mounted.
  const showCustomerBottomNav =
    view.name !== "checkout" && view.name !== "success";
  const hideQuickScroll =
    view.name === "cart" ||
    view.name === "checkout" ||
    view.name === "success" ||
    view.name === "bonuses" ||
    view.name === "search";
  const hideFloatingManager =
    view.name === "success" ||
    view.name === "checkout" ||
    view.name === "bonuses" ||
    view.name === "order-detail" ||
    view.name === "invite-friends";

  const bottomNavActive: CustomerBottomNavId =
    view.name === "search"
      ? "search"
      : view.name === "cart"
        ? "cart"
          : view.name === "profile" ||
            view.name === "profile-edit" ||
            view.name === "delivery-addresses" ||
            view.name === "buyer-profiles" ||
            view.name === "orders" ||
            view.name === "messages" ||
            view.name === "order-detail" ||
            view.name === "invite-friends" ||
            view.name === "bonuses"
          ? "cabinet"
          : "home";

  const onBottomNav = (id: CustomerBottomNavId) => {
    if (id === "home") home();
    else if (id === "search") openSearch("");
    else if (id === "cart") openCart();
    else openProfile();
  };

  const chromeBack = () => {
    const hasCabinetHistoryState = (() => {
      try {
        return Boolean(window.history.state?.sv2LegalHub || window.history.state?.sv2CabinetPanel);
      } catch {
        return false;
      }
    })();
    const action = resolveChromeBackAction(stack, hasCabinetHistoryState);
    if (action === "noop_root") return;
    if (action === "cabinet_history") {
      try {
        window.history.back();
        return;
      } catch {
        /* fall through to logical parent */
      }
    }
    if (action === "logical_parent") {
      goLogicalParentRef.current(view.name);
      return;
    }
    if (view.name === "success") {
      const unpaidNovapay =
        view.paymentMethod === "novapay_card" && novapayLifecycleStatus !== "paid" && view.status !== "paid";
      if (unpaidNovapay) {
        tryReferralExitRescue(() => navigateAwayFromPendingPayment("orders"));
        return;
      }
      tryReferralExitRescue(navigateHomeNow);
      return;
    }
    back();
  };

  return (
    <div
      className="sv2-root"
      data-store-scroll-root="true"
      data-production-boot-ready="true"
      data-hide-contactbar={hideFloatingManager ? "1" : "0"}
      data-has-bottom-nav={showCustomerBottomNav ? "1" : "0"}
    >
      <OwnerBuildIdentityChipV2 buildId={buildId} />
      <Chrome
        title={title}
        cartCount={cartCount}
        canGoBack={
          canGoBack ||
          view.name === "profile" ||
          view.name === "profile-edit" ||
          view.name === "delivery-addresses" ||
          view.name === "buyer-profiles"
        }
        lang={lang}
        theme={theme}
        routeClass={view.name}
        visualSearchSlot={
          inlineSearchViews.has(view.name) || transactionalNoCameraViews.has(view.name)
            ? undefined
            : visualSearchNode
        }
        onBack={chromeBack}
        onHome={home}
        onCart={openCart}
        onProfile={() => undefined}
        onCabinet={openProfile}
        onLangChange={setLanguage}
        onThemeCycle={() => setTheme((prev) => cycleStoreV2Theme(prev))}
        onBrowserCloseFallback={onBrowserCloseFallback}
        onRequestExitSheet={requestExitSheet}
      />
      {content}
      <OwnerRuntimeStripV2
        buildId={buildId}
        appRev={String(import.meta.env.VITE_APP_REV ?? "")}
          bootstrap={customerBootstrap ?? getCachedCustomerBootstrap()}
        consentAccepted={consentAccepted}
      />
      <QuickScrollControls lang={lang} hide={hideQuickScroll} />
      <SearchPerfDiagHud />
      {showCustomerBottomNav ? (
        <CustomerBottomNav
          lang={lang}
          active={bottomNavActive}
          cartCount={cartCount}
          onNavigate={onBottomNav}
        />
      ) : null}
      <AvailabilityRequestSheet
        lang={lang}
        open={availabilitySheetProduct != null}
        product={availabilitySheetProduct}
        displayTitle={availabilitySheetProduct ? titleOf(availabilitySheetProduct) : ""}
        prefPhone={lastCheckoutSnapshot?.phone ?? null}
        busy={availabilityBusy}
        onClose={() => setAvailabilitySheetProduct(null)}
        onSubmit={submitAvailabilitySheet}
      />
      <ExitRetentionSheetV2
        open={exitSheetOpen}
        lang={lang}
        state={exitRetentionState}
        cartCount={cartCount}
        referralAvailable={Boolean(referral)}
        recentProducts={exitSheetRecentProducts}
        onContinueShopping={() => setExitSheetOpen(false)}
        onOpenCart={() => {
          setExitSheetOpen(false);
          openCart();
        }}
        onOpenInvite={() => {
          setExitSheetOpen(false);
          openInviteFriends();
        }}
        onOpenProduct={(id) => {
          setExitSheetOpen(false);
          openProduct(id);
        }}
        onConfirmClose={() => {
          setExitSheetOpen(false);
          // Preserve the pre-existing surface_close_clicked telemetry contract even though
          // this path now bypasses GlobalAppCloseButton's own onAnalytics callback.
          emitEvent("surface_close_clicked", {
            surface: "customer",
            route_class: view.name,
            telegram_context: isTelegramMiniAppContext(),
            dirty: exitRetentionState === "cart" || exitRetentionState === "checkout",
            result: "closed_via_exit_sheet",
            build_id: STORE_V2_BUILD_ID,
          });
          finishCloseStore();
        }}
        onDismiss={() => setExitSheetOpen(false)}
      />
      {browserExitOpen ? (
        <div
          className="sv2-browser-exit-screen"
          data-testid="sv2-browser-exit-screen"
          role="dialog"
          aria-modal="true"
          aria-label={lang === "en" ? "Close store" : "Закрити магазин"}
        >
          <div className="sv2-browser-exit-card">
            <div className="sv2-browser-exit-title">
              {lang === "en" ? "Close this window" : "Закрийте це вікно"}
            </div>
            <div className="sv2-browser-exit-hint">
              {lang === "en" ? STORE_V2_BROWSER_CLOSE_HINT_EN : STORE_V2_BROWSER_CLOSE_HINT_UA}
            </div>
            <button type="button" className="sv2-cta" onClick={() => setBrowserExitOpen(false)}>
              {lang === "en" ? "Stay in store" : "Залишитися в магазині"}
            </button>
          </div>
        </div>
      ) : null}
      {view.name !== "dropshipping" && !hideFloatingManager ? (
        <ManagerContactButton
          lang={lang}
          buttonRef={contactButtonRef}
          onContact={() => {
            if (view.name === "product") {
              const p = productById.get(view.productId);
              contactManager("product", undefined, {
                product_id: p?.id,
                sku: p?.sku,
                title: p ? titleOf(p) : undefined,
                brand: p?.brand,
                category: p?.categoryName,
              });
              return;
            }
            if (view.name === "category") {
              contactManager("category", undefined, { category: tileName(view.categoryId, lang) });
              return;
            }
            if (view.name === "brand") {
              contactManager("brand", undefined, {
                brand: brands.find((b) => b.slug === view.slug)?.name ?? view.slug,
              });
              return;
            }
            // success/checkout/bonuses use in-page manager actions (floating button hidden).
            contactManager(view.name);
          }}
          toast={contactToast}
        />
      ) : null}
      <Toast message={toast} />
      <ContactManagerModal
        lang={lang}
        open={contactModal}
        phoneTel={STORE_INFO.phoneTel}
        phoneDisplay={STORE_INFO.phoneDisplay}
        telegramUrl={resolveManagerPublicUrl()}
        {...(contactContext ? { analytics: contactContext as import("./contact-actions-v2.js").ContactSourceContext } : {})}
        returnFocusRef={contactButtonRef}
        onClose={() => setContactModal(false)}
        onExplicitContact={emitExplicitManagerContact}
      />
      {diagEnabled ? (
        <>
          <div className="sv2-badge">
            <button type="button" className="sv2-diag-open" onClick={() => setShowDiag(true)}>
              🩺
            </button>
            <span>{buildId}</span>
          </div>
          {showDiag ? (
            <DiagnosticsV2
              buildId={buildId}
              consentAccepted={consentAccepted}
              onResetCache={resetStoreCache}
              onClose={() => setShowDiag(false)}
            />
          ) : null}
        </>
      ) : null}
      {String(import.meta.env.VITE_STAGING_DIAGNOSTICS ?? "").trim() === "1" ? (
        <StagingHardRestoreDiagnostics
          buildId={buildId}
          checkoutComponent="CheckoutV2View"
          cartLineCount={cart.reduce((s, i) => s + i.qty, 0)}
        />
      ) : null}
    </div>
  );
}

/** True when the Telegram WebView opened an isolated Store V2 route. */
export function isStoreV2Route(): boolean {
  try {
    const path = window.location.pathname.toLowerCase();
    const host = window.location.hostname.toLowerCase();
    const isProductionHost =
      host === "app.asiastyle.com.ua" ||
      host === "production.asiastyle-mini-app.pages.dev" ||
      (host.endsWith(".asiastyle-mini-app.pages.dev") &&
        !host.startsWith("staging.") &&
        String(import.meta.env.VITE_APP_ENV ?? "").toLowerCase() === "production");

    // COMMERCE-LAUNCH-164A-R2 — production Store is served at `/` (never require /staging-store-v2/).
    if (isProductionHost) {
      if (path === "/" || path === "" || path.startsWith("/payment-return")) return true;
    }
    if (String(import.meta.env.VITE_APP_ENV ?? "").toLowerCase() === "production") {
      if (path === "/" || path === "" || path.startsWith("/payment-return")) return true;
    }

    // BANK-IBAN-R3.7 — staging Customer build serves Store V2 at BOTH
    // `/` and `/staging-store-v2/` (two Telegram entrypoints, same artifact).
    const isStagingCustomerBuild =
      String(import.meta.env.VITE_STAGING_DIAGNOSTICS ?? "") === "1" ||
      host === "staging.asiastyle-mini-app.pages.dev";
    if (isStagingCustomerBuild) {
      if (
        path === "/" ||
        path === "" ||
        path.startsWith("/payment-return") ||
        path.includes("staging-store-v2")
      ) {
        return true;
      }
    }

    // Staging / legacy isolated routes.
    if (path.includes("staging-store-v2") || path.includes("s2-105") || /\/s2-\d+/.test(path)) return true;
    const params = new URLSearchParams(window.location.search);
    const start = (params.get("tgWebAppStartParam") ?? params.get("start_param") ?? "").toLowerCase();
    if (start.includes("store-v2") || start.includes("store_v2") || start.includes("s2-105")) return true;
    if (start === "orders" || start.startsWith("orders") || start.includes("payret") || start.includes("referral")) {
      if (isProductionHost || String(import.meta.env.VITE_APP_ENV ?? "").toLowerCase() === "production") {
        return true;
      }
    }
    const build = (params.get("build") ?? params.get("v") ?? "").toLowerCase();
    return (
      build.includes("store-v2") ||
      build.includes("hard-recovery") ||
      build.includes("s2-105") ||
      build.includes("commerce-launch-164a")
    );
  } catch {
    return false;
  }
}

// referenced to keep tile list tree-shake-safe in isolated route
void STORE_V2_TILES;
